import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppFrame } from './components/AppFrame';
import { AltYPanelHost } from './components/AltYPanelHost';
import { ClaudeIcon } from './components/AltYPanel';
import { FactoryCanvas } from './components/FactoryCanvas';
import { DiscoveryCanvas } from './components/DiscoveryCanvas';
import { IdeaList } from './components/IdeaList';
import { ApartmentList } from './components/ApartmentList';
import { BmadWorkflowView } from './components/BmadWorkflowView';
import { Login } from './components/Login';
import { OnlineDevPanel } from './components/OnlineDevPanel';
import { ProcessMapCanvas } from './components/ProcessMapCanvas';
import { WorkspaceCanvas, type WorkspaceCanvasBadge } from './components/WorkspaceCanvas';
import { loadAltYBootstrap, readSynchronousAltYBootstrap } from './lib/bootstrap';
import { formatAssistantCanvasChat, latestAssistantText } from './lib/canvasBlocks';
import { findLatestProcessMap, processMapToFlow, serializeProcessMap } from './lib/processMap';
import {
  EDITABLE_BUILTIN_PIN_IDS,
  PINNED_AGENTS,
  getPinnedBuiltinIds,
  setBuiltinPinned,
  createUserAgent,
  deleteUserAgent,
  flowEquals,
  formatFlowAsPrimer,
  formatPinEditPrimer,
  getBuiltinEditChatId,
  isIdeaAgent,
  isApartmentAgent,
  isBmadWorkflowAgent,
  isDiscoveryAgent,
  listUserAgents,
  migrateLegacyUserAgents,
  setBuiltinEditChatId,
  updateUserAgent,
} from './lib/pinnedAgents';
import type { PinnedAgent } from './lib/pinnedAgents';
import { addIdea, deleteIdea, getBmadBuild, listIdeas, startBmadBuild } from './lib/altYApi';
import { addApartment, deleteApartment, listApartments } from './lib/altYApi';
import { hasChatSelection } from './lib/chatSelection';
import { useAltYChat } from './lib/useAltYChat';
import { useAltYState } from './lib/useAltYState';
import { useBridgeContext } from './lib/useBridgeContext';
import { useIsMobile } from './lib/useIsMobile';
import type { AltYApartment, AltYBootstrap, AltYBmadBuildState, AltYBmadWorkflow, AltYBridgeContext, AltYCanvasContent, AltYChatMessage, AltYDiscoveryDoc, AltYEnvironmentTab, AltYIdea, AltYImageAttachment, AltYMode, FlowDoc, ProcessMapDoc } from './types/global';

// Modes that share a chat's broker workspace + claude --resume session (only
// the per-turn prompt wrapper differs). Switching among these mid-chat happens
// IN PLACE — the conversation continues with the newly-picked agent instead of
// forking a fresh chat. The dev-orchestrator modes (alty-dev / online-dev-mvp)
// own dedicated git worktrees / dev forks, so switching into them must still
// start a new chat. Mirrors `_WORKSPACE_COMPATIBLE_MODES` in the broker
// (/opt/onesheet-agent-y/main.py).
const WORKSPACE_COMPATIBLE_MODES = new Set<AltYMode>(['interact', 'agent-factory', 'bmad', 'chat']);
function canSwitchAgentInPlace(from: AltYMode, to: AltYMode): boolean {
  return WORKSPACE_COMPATIBLE_MODES.has(from) && WORKSPACE_COMPATIBLE_MODES.has(to);
}

// Match an initialUrl against a tab by origin — initialUrl is usually a deep
// link like https://ai.idsys.ee/Documents/123, the tab url is just the root.
function tabIdForUrl(url: string, tabs: AltYEnvironmentTab[]): string | null {
  try {
    const origin = new URL(url, window.location.href).origin;
    return tabs.find((t) => tabOrigin(t.url) === origin)?.id ?? null;
  } catch {
    return null;
  }
}

function tabOrigin(url: string): string | null {
  if (!url) return null;
  try {
    return new URL(url, window.location.href).origin;
  } catch {
    return null;
  }
}

function absoluteUrl(url: string, baseUrl?: string | null): string | null {
  try {
    return new URL(url, baseUrl || window.location.href).href;
  } catch {
    return null;
  }
}

function sameUrl(a?: string | null, b?: string | null): boolean {
  const aa = absoluteUrl(a || '');
  const bb = absoluteUrl(b || '');
  return !!aa && !!bb && aa === bb;
}

function waitForBridgeContext(frame: HTMLIFrameElement, origin: string, timeoutMs = 3500): Promise<AltYBridgeContext | null> {
  return new Promise((resolve) => {
    let settled = false;
    const timer = window.setTimeout(() => finish(null), timeoutMs);
    const retryTimer = window.setTimeout(requestContext, 250);

    function finish(context: AltYBridgeContext | null) {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      window.clearTimeout(retryTimer);
      window.removeEventListener('message', onMessage);
      resolve(context);
    }

    function requestContext() {
      try {
        frame.contentWindow?.postMessage({ type: 'idsys.alty.shell.command', action: 'context' }, origin);
      } catch {
        /* The frame may be unavailable during navigation. */
      }
    }

    function onMessage(event: MessageEvent) {
      if (event.source !== frame.contentWindow) return;
      if (event.origin !== origin) return;
      const data = event.data as AltYBridgeContext | null;
      if (!data || data.type !== 'idsys.alty.bridge' || data.action !== 'context') return;
      finish(data);
    }

    window.addEventListener('message', onMessage);
    requestContext();
  });
}

function isLoggedOutContext(context: AltYBridgeContext | null): boolean {
  if (!context) return true;
  const haystack = `${context.url || ''} ${context.path || ''} ${context.title || ''}`.toLowerCase();
  return /\/account\/login|\/identity\/account\/login|\/login\b|log in|login|sign in|signin/.test(haystack);
}

// Extract the latest FlowDoc the agent emitted as a ```json block from its
// chat messages. Returns null if no message contains a valid one.
function findLatestFlow(messages: AltYChatMessage[]): FlowDoc | null {
  const re = /```json\s*([\s\S]*?)```/i;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== 'assistant' || !message.text) continue;
    const match = message.text.match(re);
    if (!match) continue;
    try {
      const parsed = JSON.parse(match[1]);
      if (parsed && typeof parsed === 'object' && typeof parsed.title === 'string' && Array.isArray(parsed.steps)) {
        return parsed as FlowDoc;
      }
    } catch {
      /* not a flow doc — keep searching */
    }
  }
  return null;
}

// The apartment finder ends its reply with a fenced ```json``` block of the
// shape { "alty_apartments": [ {...}, ... ] } (see APARTMENT_AGENT_PRIMER). Scan
// the given messages newest-first for the most recent such block and return its
// array — that's what the panel persists. Distinct marker key so it never
// collides with a FlowDoc / process-map block. Returns [] when none is found
// (e.g. the agent reported no qualifying listings).
function findLatestApartments(messages: AltYChatMessage[]): AltYApartment[] {
  const re = /```json\s*([\s\S]*?)```/gi;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== 'assistant' || !message.text) continue;
    const blocks = message.text.match(re);
    if (!blocks) continue;
    // A reply may contain several fenced blocks; check each, last one wins.
    for (let b = blocks.length - 1; b >= 0; b--) {
      const inner = blocks[b].replace(/```json\s*/i, '').replace(/```$/, '');
      try {
        const parsed = JSON.parse(inner);
        if (parsed && typeof parsed === 'object' && Array.isArray(parsed.alty_apartments)) {
          return parsed.alty_apartments.filter(
            (a: unknown): a is AltYApartment => !!a && typeof a === 'object',
          );
        }
      } catch {
        /* not the apartments block — keep looking */
      }
    }
  }
  return [];
}

// The Customized BMad Agent Flow re-emits the WHOLE workflow each turn as a
// fenced ```json``` block of the shape { "bmad_workflow": {...} } (see
// BMAD_WORKFLOW_PRIMER). Scan newest-first for the most recent such block and
// return its object — that's what the right-side tabs render. Distinct marker
// key so it never collides with a FlowDoc / process-map / apartments block.
function findLatestBmadWorkflow(messages: AltYChatMessage[]): AltYBmadWorkflow | null {
  const re = /```json\s*([\s\S]*?)```/gi;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== 'assistant' || !message.text) continue;
    if (!message.text.includes('bmad_workflow')) continue;
    const blocks = message.text.match(re);
    if (!blocks) continue;
    for (let b = blocks.length - 1; b >= 0; b--) {
      const inner = blocks[b].replace(/```json\s*/i, '').replace(/```$/, '');
      try {
        const parsed = JSON.parse(inner);
        if (parsed && typeof parsed === 'object' && parsed.bmad_workflow
          && typeof parsed.bmad_workflow === 'object') {
          return parsed.bmad_workflow as AltYBmadWorkflow;
        }
      } catch {
        /* not the workflow block — keep looking */
      }
    }
  }
  return null;
}

// The Goal Discovery agent ends its reply with a fenced ```json``` block of the
// shape { "alty_discovery": { rephrase, goalGaps, questions, userStory, design,
// ... } } (see DISCOVERY_AGENT_PRIMER). Scan newest-first for the most recent
// such block and return its object — that's what the DiscoveryCanvas renders as
// tabs. Distinct marker key so it never collides with a FlowDoc / process-map /
// apartments block. Returns null when none is found.
function findLatestDiscovery(messages: AltYChatMessage[]): AltYDiscoveryDoc | null {
  const re = /```json\s*([\s\S]*?)```/gi;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== 'assistant' || !message.text) continue;
    const blocks = message.text.match(re);
    if (!blocks) continue;
    // A reply may contain several fenced blocks; check each, last one wins.
    for (let b = blocks.length - 1; b >= 0; b--) {
      const inner = blocks[b].replace(/```json\s*/i, '').replace(/```$/, '');
      try {
        const parsed = JSON.parse(inner);
        if (parsed && typeof parsed === 'object' && parsed.alty_discovery
          && typeof parsed.alty_discovery === 'object') {
          return parsed.alty_discovery as AltYDiscoveryDoc;
        }
      } catch {
        /* not the discovery block — keep looking */
      }
    }
  }
  return null;
}

// Canvas mode (no online environment tab selected): the AI uses the now-empty
// left pane as an extra display surface by ending its reply with a fenced
// ```json``` block of shape { "alty_canvas": { html | image | title | caption } }.
// Scan newest-first for the most recent such block — the latest emitted canvas
// wins. Returns null when no message in the chat has emitted one yet.
function findLatestCanvas(messages: AltYChatMessage[]): AltYCanvasContent | null {
  const re = /```json\s*([\s\S]*?)```/gi;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== 'assistant' || !message.text) continue;
    if (!message.text.includes('alty_canvas')) continue;
    // Cheap pre-check before the global regex: the regex needs a CLOSING ```
    // after the opening one. Mid-stream (open ```json fence still growing), no
    // close exists yet → the regex would scan the entire growing message text
    // only to return null. On every rAF tick that adds up to quadratic work,
    // which is what made canvas-mode streaming feel slow even after the
    // `formatAssistantCanvasChat` short-circuit landed.
    const firstFence = message.text.indexOf('```');
    if (firstFence < 0 || message.text.indexOf('```', firstFence + 3) < 0) continue;
    const blocks = message.text.match(re);
    if (!blocks) continue;
    for (let b = blocks.length - 1; b >= 0; b--) {
      const inner = blocks[b].replace(/```json\s*/i, '').replace(/```$/, '');
      try {
        const parsed = JSON.parse(inner);
        if (parsed && typeof parsed === 'object' && parsed.alty_canvas
          && typeof parsed.alty_canvas === 'object') {
          return parsed.alty_canvas as AltYCanvasContent;
        }
      } catch {
        /* not a canvas block — keep looking */
      }
    }
  }
  return null;
}

// Per-turn primer prepended to the user message when the canvas is the active
// display surface (no env tab selected). Tells the AI what shape to emit so the
// canvas updates, and reminds Claude turns about the existing image-gen MCP.
function canvasModePrimer(surface: 'empty' | 'canvas-only' = 'empty'): string {
  const opening = surface === 'canvas-only'
    ? 'This chat is in Canvas-only mode: the user sees ONLY the canvas — there is no chat transcript on screen. Treat the canvas as your entire answer; the chat bubble is effectively invisible, so everything the user needs must be on the canvas.\n\n'
    : 'The user has no online environment selected, so the left side of the shell is a blank canvas you can render content onto. When it helps to show information in a bigger or richer format than chat, put the detail on the canvas — not in chat.\n\n';
  return (
    opening
    + (surface === 'canvas-only'
      ? 'Be ruthless about brevity. Show ONLY what the user most likely wants — the result itself — in the single form that is fastest to grasp. No preamble, no restating the question, no methodology, no caveats, no "here is…", no next-step suggestions unless asked. Keep the chat bubble empty or one short sentence at most.\n\n'
      : '')
    + 'Reply rules:\n'
    + '- Keep the chat bubble short: one or two sentences that say what you did '
    + 'or point the user to the canvas. Do not paste long explanations in chat.\n'
    + '- Put tables, multi-step write-ups, mockups, and anything longer than a '
    + 'brief summary in a fenced ```json``` canvas block:\n\n'
    + '```json\n{"alty_canvas": {"id": "main", "title": "Optional title", "html": "<div>…your HTML…</div>"}}\n```\n\n'
    + 'Canvas rules:\n'
    + '- Your ```json canvas block is hidden from chat; the UI shows a short '
    + '"Drawing canvas" / "Canvas drawn" badge instead.\n'
    + '- `html` is rendered inside a sandboxed iframe (scripts are blocked). '
    + 'Plain HTML + inline `<style>` only; no external CSS or JS.\n'
    + '- For an image, either embed `<img src="https://…">` inside `html`, or '
    + 'set `image` to a single URL — both are fine.\n'
    + '- For a generated image, call the openai-images MCP tool '
    + '`generate_bmad_mockup` (uses ChatGPT image 2.0 / gpt-image-2, extremely '
    + 'detailed). Take the `url` from its result and put it in the canvas '
    + 'block as `image` or inside `html`.\n'
    + '- Emit a canvas block when the answer is more than a couple of sentences. '
    + 'If chat alone is enough, reply normally with no block.\n'
    + '- Never duplicate the same content in both chat and canvas.\n'
    + '- Reuse the same `id` to replace that canvas surface, or use a new `id` to identify a distinct surface for newer clients.\n'
    + '- Always re-emit the WHOLE block — only the latest compatible canvas content is shown.\n\n'
    + 'Canvas style — match OneSheet\'s clean light look (NOT a dark dashboard): '
    + 'white background, dark text, sections wrapped in bordered cards with a '
    + 'light-gray header strip, generous padding, inline `<code>` for identifiers, '
    + 'bullet lists with bold lead phrases. Tight example:\n\n'
    + '```html\n'
    + '<style>body{font-family:system-ui,sans-serif;color:#1f2937;background:#fff;line-height:1.55;margin:0;padding:20px;font-size:14px}h1,h2,h3{margin:0}p{margin:0 0 12px}ul{margin:0;padding-left:20px}li{margin-bottom:6px}code{background:#f3f4f6;padding:2px 6px;border-radius:4px;font-size:0.9em;font-family:ui-monospace,monospace}.card{border:1px solid #e5e7eb;border-radius:8px;margin-bottom:16px;overflow:hidden}.card>h3{padding:12px 16px;background:#f9fafb;border-bottom:1px solid #e5e7eb;font-size:15px;font-weight:600}.card>.body{padding:16px}</style>\n'
    + '<p>Short lede paragraph framing the answer.</p>\n'
    + '<div class="card"><h3>Section title</h3><div class="body"><ul><li><b>Key term:</b> explanation with inline <code>identifier</code>.</li></ul></div></div>\n'
    + '```'
  );
}

function AltYShellApp({
  bootstrap,
  reloadBootstrap,
}: {
  bootstrap: AltYBootstrap;
  reloadBootstrap: () => Promise<AltYBootstrap>;
}) {
  // Responsive fold: below the phone breakpoint /AltY renders ONLY the chat
  // panel — no environment tabs, no OneSheet iframe, no factory/process-map
  // canvas. The same bundle is the desktop shell at wider widths.
  const isMobile = useIsMobile();
  const config = useMemo(() => bootstrap.shell, [bootstrap.shell]);
  const initialTabId = useMemo(
    () => tabIdForUrl(config.initialUrl, config.tabs) ?? config.tabs[0]?.id ?? '',
    [config],
  );
  // For the tab the user arrived on, use the deep-link initialUrl as the iframe
  // src so they land on the actual page; other tabs use the bare host url.
  const initialTabSrc = useMemo(() => {
    const t = config.tabs.find((tab) => tab.id === initialTabId);
    if (!t) return config.initialUrl;
    return tabOrigin(config.initialUrl) === tabOrigin(t.url) ? config.initialUrl : t.url;
  }, [config, initialTabId]);

  const frameRefs = useRef<Map<string, HTMLIFrameElement | null>>(new Map());
  const frameLoadResolversRef = useRef<Map<string, (frame: HTMLIFrameElement | null) => void>>(new Map());
  const [tabSrcById, setTabSrcById] = useState<Record<string, string>>(() => {
    const urls: Record<string, string> = {};
    for (const tab of config.tabs) {
      urls[tab.id] = tab.id === initialTabId ? initialTabSrc : tab.url;
    }
    return urls;
  });
  const [activeTabId, setActiveTabId] = useState(initialTabId);
  const [loadedTabIds, setLoadedTabIds] = useState<Set<string>>(() => new Set(initialTabId ? [initialTabId] : []));
  const [activeFrame, setActiveFrame] = useState<HTMLIFrameElement | null>(null);
  // Mobile only: the phone shell shows the chat OR the app/dev iframes, never
  // both at once. `mobileView` is which is on top; `mobileAppMounted` flips
  // true the first time the user opens the app view so we don't load OneSheet
  // in the background for chat-only sessions, then keep the iframes mounted so
  // toggling back and forth doesn't reload them.
  const [mobileView, setMobileView] = useState<'chat' | 'app'>('chat');
  const [mobileAppMounted, setMobileAppMounted] = useState(false);
  // Mobile only: the chat panel's settings (cog) popover open flag, lifted so
  // the app-view bar's own cog can flip back to the chat view and pop it open.
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);

  const allowedOrigins = useMemo(() => {
    const set = new Set<string>();
    for (const tab of config.tabs) {
      const origin = tabOrigin(tab.url);
      if (origin) set.add(origin);
    }
    return set;
  }, [config]);

  const altYState = useAltYState();
  const bridgeContext = useBridgeContext(activeFrame, allowedOrigins);
  const [authed, setAuthed] = useState<boolean | null>(bootstrap.authenticated);

  const activeTab = useMemo(
    () => config.tabs.find((tab) => tab.id === activeTabId) ?? null,
    [config.tabs, activeTabId],
  );
  const waitForTabFrame = useCallback((tabId: string, timeoutMs = 1500): Promise<HTMLIFrameElement | null> => {
    const existing = frameRefs.current.get(tabId) ?? null;
    if (existing) return Promise.resolve(existing);
    return new Promise((resolve) => {
      const started = Date.now();
      function tick() {
        const frame = frameRefs.current.get(tabId) ?? null;
        if (frame || Date.now() - started >= timeoutMs) {
          resolve(frame);
          return;
        }
        window.requestAnimationFrame(tick);
      }
      tick();
    });
  }, []);

  const waitForNextFrameLoad = useCallback((tabId: string, timeoutMs = 15000): Promise<HTMLIFrameElement | null> => {
    return new Promise((resolve) => {
      let settled = false;
      const timer = window.setTimeout(() => finish(frameRefs.current.get(tabId) ?? null), timeoutMs);
      function finish(frame: HTMLIFrameElement | null) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        frameLoadResolversRef.current.delete(tabId);
        resolve(frame);
      }
      frameLoadResolversRef.current.set(tabId, finish);
    });
  }, []);

  const frameForBaseUrl = useCallback((baseUrl: string | null): HTMLIFrameElement | null => {
    const origin = tabOrigin(baseUrl || '');
    if (!origin) return activeFrame;
    const tab = config.tabs.find((candidate) => tabOrigin(candidate.url) === origin);
    return tab ? frameRefs.current.get(tab.id) ?? null : activeFrame;
  }, [activeFrame, config.tabs]);

  const navigateEnvironmentUrl = useCallback(async (url: string) => {
    const targetUrl = absoluteUrl(url, activeTab?.url);
    const targetOrigin = tabOrigin(targetUrl || '');
    const tab = targetOrigin
      ? config.tabs.find((candidate) => tabOrigin(candidate.url) === targetOrigin)
      : null;
    if (!targetUrl || !targetOrigin || !tab) {
      return {
        ok: false,
        url,
        baseUrl: targetOrigin || undefined,
        tab: null,
        error: `No Alt-Y environment tab is configured for ${targetOrigin || url}.`,
      };
    }

    const wasLoaded = loadedTabIds.has(tab.id);
    const currentSrc = tabSrcById[tab.id] || tab.url;
    const needsNavigation = !sameUrl(currentSrc, targetUrl);
    const loadPromise = needsNavigation || !wasLoaded
      ? waitForNextFrameLoad(tab.id)
      : waitForTabFrame(tab.id);

    setTabSrcById((prev) => (prev[tab.id] === targetUrl ? prev : { ...prev, [tab.id]: targetUrl }));
    setLoadedTabIds((prev) => {
      if (prev.has(tab.id)) return prev;
      const next = new Set(prev);
      next.add(tab.id);
      return next;
    });
    setActiveTabId(tab.id);
    if (isMobile) {
      setMobileAppMounted(true);
      setMobileView('app');
    }

    const frame = await loadPromise;
    if (!frame) {
      return {
        ok: false,
        url: targetUrl,
        baseUrl: targetOrigin,
        tab,
        error: `Could not load the ${tab.label} tab. Please open ${tab.label} and log in, then ask me to continue.`,
      };
    }

    const context = await waitForBridgeContext(frame, targetOrigin);
    if (isLoggedOutContext(context)) {
      return {
        ok: false,
        url: targetUrl,
        baseUrl: targetOrigin,
        tab,
        error: `The ${tab.label} tab is not logged in or did not expose the Alt-Y bridge. Please log in to ${targetOrigin}, then ask me to continue.`,
      };
    }

    return {
      ok: true,
      url: context?.url || targetUrl,
      baseUrl: targetOrigin,
      tab,
    };
  }, [activeTab?.url, config.tabs, isMobile, loadedTabIds, tabSrcById, waitForNextFrameLoad, waitForTabFrame]);

  const chat = useAltYChat({
    isOpen: altYState.isOpen,
    context: bridgeContext,
    appFrame: activeFrame,
    activeTab,
    frameForBaseUrl,
    onCrossServerNavigate: navigateEnvironmentUrl,
  });
  const isFactoryMode = chat.mode === 'agent-factory';
  const isOnlineDevMvpMode = chat.mode === 'online-dev-mvp';
  // RunId for which the user dismissed the Online Dev MVP iframe panel via X.
  // Re-opening the same chat keeps it hidden until a new chat is attached;
  // switching to another chat clears it.
  const [closedDevEnvRunId, setClosedDevEnvRunId] = useState<number | null>(null);
  useEffect(() => {
    if (closedDevEnvRunId != null && chat.activeRunId !== closedDevEnvRunId) {
      setClosedDevEnvRunId(null);
    }
  }, [chat.activeRunId, closedDevEnvRunId]);
  // Show the dev-fork iframe whenever THIS chat has a live dev env — not just
  // in online-dev-mvp mode. An AUTO chat that dispatched an online build
  // sub-run also gets a `_dev_env` event, and we want its fork on the right
  // too. `chat.devEnv` is only ever populated by that event, so this stays
  // false for ordinary chats.
  const showOnlineDevPanel = !!chat.devEnv
    && !!chat.devEnv.url
    && closedDevEnvRunId !== chat.activeRunId;
  const [selectedPinnedAgentId, setSelectedPinnedAgentId] = useState<string | null>(null);
  // Multi-select recipient list. Empty array = route to the picked agent;
  // non-empty = bypass the agent and broadcast as human messages to each.
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  // The pin the user has explicitly chosen to EDIT (via the hover-revealed
  // edit button on a pin row). Drives FactoryCanvas visibility — selection
  // alone no longer surfaces the diagram. Cleared when the user starts a new
  // chat, attaches a different chat, or sends a message in interact mode.
  const [editingAgentId, setEditingAgentId] = useState<string | null>(null);
  const [userAgents, setUserAgents] = useState<PinnedAgent[]>([]);
  // Built-ins are unpinned by default; this set tracks the ones the user has
  // pinned from the button above each diagram.
  const [pinnedBuiltins, setPinnedBuiltins] = useState<Set<string>>(() => getPinnedBuiltinIds());
  // The agent picker's current selection — sticky across reloads. 'auto' lets
  // the turn itself self-route via AUTO_PROMPT (see handleSendMessage below).
  const [selectedAgentId, setSelectedAgentId] = useState<string>(() => {
    try {
      return localStorage.getItem('altY.selectedAgentId.v1') || 'auto';
    } catch {
      return 'auto';
    }
  });
  useEffect(() => {
    try { localStorage.setItem('altY.selectedAgentId.v1', selectedAgentId); } catch { /* ignore */ }
  }, [selectedAgentId]);
  // NB: the picker selection is sticky and persisted (above) and only ever
  // changes on its own when the user switches to a PREVIOUS chat — never on a
  // new chat, reconnect, mode switch, or a finished turn. Switching to a chat
  // that has a remembered agent + model restores both (the model side lives in
  // AltYPanel's restore effect, keyed on chat.activeRunId; see chatSelection).
  // Chats with no memory (started before this existed) fall back to the
  // mode-derived agent in handleAttachChat below so the picker still lines up.
  // Bumped after the local built-in editor-chat binding changes so the
  // `useMemo` calls that read localStorage re-run immediately.
  const [builtinEditRev, setBuiltinEditRev] = useState(0);
  // Built-ins carry a computed `pinned` from the user's pinned-builtins set
  // (default false) so the sidebar's `pinned !== false` filter hides them
  // until the user pins them; the AgentPicker dropdown still lists them all.
  const allPinnedAgents = useMemo(
    () => [
      ...PINNED_AGENTS.map((a) => ({ ...a, pinned: pinnedBuiltins.has(a.id) })),
      ...userAgents,
    ],
    [userAgents, pinnedBuiltins],
  );

  // --- Idea Agent: capture + idea list -------------------------------------
  // When the selected agent is an Idea Agent (recognised by title), the panel
  // captures each sent message as an idea (handleSendMessage below) into a
  // per-(agent, department) list on the broker, and offers an "ideas" toggle
  // that flips the right pane / a mobile overlay to that list. Department is
  // the OneSheet customer the panel is bound to, so each customer keeps its own.
  const selectedAgent = useMemo(
    () => allPinnedAgents.find((a) => a.id === selectedAgentId) ?? null,
    [allPinnedAgents, selectedAgentId],
  );
  const isIdeaAgentActive = isIdeaAgent(selectedAgent);
  const ideaAgentId = selectedAgent?.id ?? null;
  const ideaDepartment = bridgeContext?.customerId ? String(bridgeContext.customerId) : 'default';
  const [ideas, setIdeas] = useState<AltYIdea[]>([]);
  const [ideasView, setIdeasView] = useState(false);
  const [ideaToast, setIdeaToast] = useState<{ text: string; tone: 'added' | 'dupe' } | null>(null);
  // Leaving the Idea Agent closes the ideas view so the iframe / conversation
  // comes back (the toggle that re-opens it is only shown for idea agents).
  useEffect(() => {
    if (!isIdeaAgentActive) setIdeasView(false);
  }, [isIdeaAgentActive]);
  // Load the saved ideas whenever an Idea Agent is active (and on department /
  // agent change). Cheap GET; the broker scopes by owner + agent + department.
  useEffect(() => {
    if (!authed || !isIdeaAgentActive || !ideaAgentId) return;
    let cancelled = false;
    void listIdeas(ideaAgentId, ideaDepartment).then((list) => {
      if (!cancelled) setIdeas(list);
    });
    return () => { cancelled = true; };
  }, [authed, isIdeaAgentActive, ideaAgentId, ideaDepartment]);
  // Auto-dismiss the "idea added" toast.
  useEffect(() => {
    if (!ideaToast) return;
    const timer = window.setTimeout(() => setIdeaToast(null), 2600);
    return () => window.clearTimeout(timer);
  }, [ideaToast]);
  const refreshIdeas = useCallback(() => {
    if (!ideaAgentId) return;
    void listIdeas(ideaAgentId, ideaDepartment).then(setIdeas);
  }, [ideaAgentId, ideaDepartment]);
  // Capture a sent message as an idea. Called from handleSendMessage when an
  // Idea Agent is the active selection. The broker dedupes (identical text →
  // added:false), and we surface either outcome as a small toast.
  const captureIdea = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed || !ideaAgentId) return;
    void addIdea(ideaAgentId, ideaDepartment, trimmed).then((res) => {
      if (!res) return;
      setIdeaToast(res.added
        ? { text: 'Idea added', tone: 'added' }
        : { text: 'Already on your idea list', tone: 'dupe' });
      refreshIdeas();
    });
  }, [ideaAgentId, ideaDepartment, refreshIdeas]);
  const handleDeleteIdea = useCallback((id: string) => {
    setIdeas((prev) => prev.filter((i) => i.id !== id));
    void deleteIdea(id).then((ok) => { if (!ok) refreshIdeas(); });
  }, [refreshIdeas]);
  const toggleIdeasView = useCallback(() => setIdeasView((v) => !v), []);

  // --- Recent Apartment Origin Finder: capture + cards list ----------------
  // When the selected agent is the apartment finder (recognised by title), the
  // panel persists the verified listings it emits (parsed from the reply, see
  // the capture effect below) into a per-(agent, department) list on the broker,
  // and offers a "buildings" header toggle that flips the right pane / a mobile
  // overlay to the saved cards (newest first, click for the detail popup).
  const isApartmentAgentActive = isApartmentAgent(selectedAgent);
  const apartmentAgentId = selectedAgent?.id ?? null;
  const apartmentDepartment = ideaDepartment; // same per-customer scoping as ideas
  const [apartments, setApartments] = useState<AltYApartment[]>([]);
  const [apartmentsView, setApartmentsView] = useState(false);
  const [apartmentToast, setApartmentToast] = useState<{ text: string; tone: 'added' | 'dupe' } | null>(null);
  useEffect(() => {
    if (!isApartmentAgentActive) setApartmentsView(false);
  }, [isApartmentAgentActive]);
  useEffect(() => {
    if (!authed || !isApartmentAgentActive || !apartmentAgentId) return;
    let cancelled = false;
    void listApartments(apartmentAgentId, apartmentDepartment).then((list) => {
      if (!cancelled) setApartments(list);
    });
    return () => { cancelled = true; };
  }, [authed, isApartmentAgentActive, apartmentAgentId, apartmentDepartment]);
  useEffect(() => {
    if (!apartmentToast) return;
    const timer = window.setTimeout(() => setApartmentToast(null), 2800);
    return () => window.clearTimeout(timer);
  }, [apartmentToast]);
  const refreshApartments = useCallback(() => {
    if (!apartmentAgentId) return;
    void listApartments(apartmentAgentId, apartmentDepartment).then(setApartments);
  }, [apartmentAgentId, apartmentDepartment]);
  // Persist the listings the agent emitted this turn. The broker dedupes on the
  // origin/source URL, so re-finding the same place doesn't double-up; we toast
  // how many were genuinely new, then reload the cards.
  const captureApartments = useCallback((found: AltYApartment[]) => {
    if (!apartmentAgentId || !found.length) return;
    void (async () => {
      let added = 0;
      for (const apt of found) {
        const res = await addApartment(apartmentAgentId, apartmentDepartment, apt);
        if (res?.added) added += 1;
      }
      setApartmentToast(
        added > 0
          ? { text: `${added} apartment${added === 1 ? '' : 's'} added`, tone: 'added' }
          : { text: 'No new apartments', tone: 'dupe' },
      );
      refreshApartments();
    })();
  }, [apartmentAgentId, apartmentDepartment, refreshApartments]);
  const handleDeleteApartment = useCallback((id: string) => {
    setApartments((prev) => prev.filter((a) => a.id !== id));
    void deleteApartment(id).then((ok) => { if (!ok) refreshApartments(); });
  }, [refreshApartments]);
  const toggleApartmentsView = useCallback(() => setApartmentsView((v) => !v), []);
  // Watch each turn in an apartment-finder chat: snapshot the message count when
  // the turn starts (busy false→true) and, once it finishes (busy true→false),
  // scan the reply for the emitted apartments block and persist it. Mirrors the
  // "improve agent" turn-boundary pattern; the snapshot avoids re-saving earlier
  // turns' results, and the broker's dedupe is a backstop if it ever does.
  const apartmentTurnStartRef = useRef<number | null>(null);
  const apartmentSawBusyRef = useRef(false);
  // Hand the effect a live handle on chat.messages so we can drop
  // `chat.messages` from its deps — otherwise it re-runs on every streaming
  // text chunk just to early-return on the busy gate.
  const chatMessagesRef = useRef(chat.messages);
  chatMessagesRef.current = chat.messages;
  useEffect(() => {
    if (!isApartmentAgentActive || !apartmentAgentId) {
      apartmentSawBusyRef.current = false;
      apartmentTurnStartRef.current = null;
      return;
    }
    if (chat.busy) {
      if (!apartmentSawBusyRef.current) {
        apartmentSawBusyRef.current = true;
        apartmentTurnStartRef.current = chatMessagesRef.current.length;
      }
      return;
    }
    if (!apartmentSawBusyRef.current) return; // no turn ran since selection
    apartmentSawBusyRef.current = false;
    const start = apartmentTurnStartRef.current ?? 0;
    apartmentTurnStartRef.current = null;
    const found = findLatestApartments(chatMessagesRef.current.slice(start));
    if (found.length) captureApartments(found);
  }, [isApartmentAgentActive, apartmentAgentId, chat.busy, captureApartments]);

  // --- Customized BMad Agent Flow: workflow tabs ---------------------------
  // When the selected agent is the BMad workflow agent (recognised by title),
  // the panel derives the living workflow object the agent re-emits each turn
  // (user story, rephrase, sources, gaps, questions, design, architecture
  // decisions, development tasks) straight from the transcript — no server-side store;
  // reopening the chat re-parses.
  // It flips the right pane (desktop) / a full-screen overlay (mobile) to the
  // tabbed BmadWorkflowView, and a Build button there asks the broker to launch
  // dependency-aware implementation subchats.
  const activeChatSummary = useMemo(
    () => chat.chats.find((item) => item.runId === chat.activeRunId) ?? null,
    [chat.chats, chat.activeRunId],
  );
  const isBmadAgentActive = isBmadWorkflowAgent(selectedAgent);
  const bmadWorkflowAgent = useMemo(
    () => allPinnedAgents.find((agent) => isBmadWorkflowAgent(agent)) ?? null,
    [allPinnedAgents],
  );
  const latestBmadWorkflow = useMemo(
    () => findLatestBmadWorkflow(chat.messages),
    [chat.messages],
  );
  const activeRootRunId = activeChatSummary?.rootRunId ?? activeChatSummary?.parentRunId ?? chat.activeRunId ?? null;
  const [bmadChildRootHint, setBmadChildRootHint] = useState<number | null>(null);
  const [bmadWorkflowSnapshot, setBmadWorkflowSnapshot] = useState<{
    runId: number;
    agentId: string;
    workflow: AltYBmadWorkflow;
  } | null>(null);
  const [bmadView, setBmadView] = useState(false);
  useEffect(() => {
    if (!latestBmadWorkflow || chat.activeRunId == null) return;
    const agentId = isBmadAgentActive
      ? selectedAgentId
      : bmadWorkflowAgent?.id;
    if (!agentId) return;
    setBmadWorkflowSnapshot({ runId: chat.activeRunId, agentId, workflow: latestBmadWorkflow });
  }, [latestBmadWorkflow, chat.activeRunId, isBmadAgentActive, selectedAgentId, bmadWorkflowAgent]);
  useEffect(() => {
    if (bmadChildRootHint == null || activeChatSummary == null) return;
    const summaryRootRunId = activeChatSummary.rootRunId ?? activeChatSummary.parentRunId ?? activeChatSummary.runId;
    if (activeChatSummary.runId !== bmadChildRootHint && summaryRootRunId === bmadChildRootHint) {
      setBmadChildRootHint(null);
    }
  }, [bmadChildRootHint, activeChatSummary]);
  useEffect(() => {
    const runId = chat.activeRunId;
    if (runId == null || !latestBmadWorkflow || !bmadWorkflowAgent) return;
    if (hasChatSelection(runId)) return;
    if (!isBmadWorkflowAgent(selectedAgent)) {
      setSelectedAgentId(bmadWorkflowAgent.id);
      setBmadView(true);
    }
  }, [chat.activeRunId, latestBmadWorkflow, bmadWorkflowAgent, selectedAgent]);
  const bmadWorkflowFromSnapshot = bmadWorkflowSnapshot
    && (activeRootRunId === bmadWorkflowSnapshot.runId || bmadChildRootHint === bmadWorkflowSnapshot.runId)
    ? bmadWorkflowSnapshot.workflow
    : null;
  const bmadWorkflowRunId = latestBmadWorkflow && chat.activeRunId != null
    ? chat.activeRunId
    : bmadWorkflowFromSnapshot && bmadWorkflowSnapshot
      ? bmadWorkflowSnapshot.runId
      : null;
  const bmadWorkflow = isBmadAgentActive ? (latestBmadWorkflow ?? bmadWorkflowFromSnapshot) : null;
  // Auto-open the tabs the first time a workflow block appears (so the pieces
  // surface without the user hunting for a toggle), but don't fight a manual
  // close on every streaming tick — only fire on the no-workflow → has-workflow
  // edge. Reset the edge tracker when the agent is deselected.
  const bmadHadWorkflowRef = useRef(false);
  useEffect(() => {
    if (!isBmadAgentActive) {
      setBmadView(false);
      bmadHadWorkflowRef.current = false;
      return;
    }
    const has = !!bmadWorkflow;
    if (has && !bmadHadWorkflowRef.current) setBmadView(true);
    bmadHadWorkflowRef.current = has;
  }, [isBmadAgentActive, bmadWorkflow]);
  const toggleBmadView = useCallback(() => setBmadView((v) => !v), []);
  const [bmadBuildState, setBmadBuildState] = useState<AltYBmadBuildState | null>(null);
  const [bmadBuildBusy, setBmadBuildBusy] = useState(false);

  useEffect(() => {
    setBmadBuildState(null);
  }, [bmadWorkflowRunId]);

  useEffect(() => {
    if (!isBmadAgentActive || !bmadWorkflow || bmadWorkflowRunId == null) return;
    let stopped = false;
    let inFlight = false;
    const runId = bmadWorkflowRunId;
    async function poll() {
      if (stopped || inFlight) return;
      inFlight = true;
      try {
        const state = await getBmadBuild(runId);
        if (!stopped) {
          setBmadBuildState(state);
          if (state) void chat.refreshChats();
        }
      } catch {
        /* build may not be started yet */
      } finally {
        inFlight = false;
      }
    }
    void poll();
    const timer = window.setInterval(poll, 3000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [isBmadAgentActive, bmadWorkflow, bmadWorkflowRunId, chat.refreshChats]);

  const handleBuildBmadWorkflow = useCallback(() => {
    if (bmadWorkflowRunId == null || !bmadWorkflow || bmadBuildBusy) return;
    setBmadBuildBusy(true);
    void startBmadBuild(bmadWorkflowRunId, bmadWorkflow)
      .then((state) => {
        setBmadBuildState(state);
        void chat.refreshChats();
      })
      .catch((error) => chat.addMeta(`BMAD build failed: ${(error as Error).message || error}`))
      .finally(() => setBmadBuildBusy(false));
  }, [bmadBuildBusy, bmadWorkflow, bmadWorkflowRunId, chat]);

  // --- Goal Discovery: brief canvas (rephrase + four pieces as tabs) --------
  // When the selected agent is the Goal Discovery agent (recognised by id), the
  // panel parses the { alty_discovery: {...} } block it emits and renders the
  // brief as tabs in the right pane (desktop) / a full-screen overlay (mobile).
  // No broker persistence — the latest emitted brief is the canvas content,
  // exactly like the agent-factory diagram rides the chat transcript.
  const isDiscoveryAgentActive = isDiscoveryAgent(selectedAgent);
  const discoveryDoc = useMemo(
    () => (isDiscoveryAgentActive ? findLatestDiscovery(chat.messages) : null),
    [isDiscoveryAgentActive, chat.messages],
  );
  const [discoveryView, setDiscoveryView] = useState(false);
  // Leaving the discovery agent closes the brief so the iframe / conversation
  // comes back (the toggle that re-opens it is only shown for that agent).
  useEffect(() => {
    if (!isDiscoveryAgentActive) setDiscoveryView(false);
  }, [isDiscoveryAgentActive]);
  // Auto-open the brief the moment a discovery turn finishes producing one, so
  // the pieces surface as soon as they're ready. Snapshot busy at turn start
  // and, when it goes true→false with a brief present, flip the view on. The
  // user can still close it; it won't pop back until the next turn.
  const discoverySawBusyRef = useRef(false);
  useEffect(() => {
    if (!isDiscoveryAgentActive) { discoverySawBusyRef.current = false; return; }
    if (chat.busy) { discoverySawBusyRef.current = true; return; }
    if (!discoverySawBusyRef.current) return;
    discoverySawBusyRef.current = false;
    if (discoveryDoc) setDiscoveryView(true);
  }, [isDiscoveryAgentActive, chat.busy, discoveryDoc]);
  const toggleDiscoveryView = useCallback(() => setDiscoveryView((v) => !v), []);

  // --- Workspace canvas: the AI's display surface when no env tab is selected.
  // Latest `alty_canvas` JSON block from the chat wins; until the AI emits one
  // the canvas shows its empty-state hint. `canvasMode` gates both rendering
  // and the per-turn primer that tells the AI the canvas exists.
  const canvasMode = activeTabId === '';
  const [canvasOnly, setCanvasOnly] = useState(() => {
    try { return localStorage.getItem('altY.canvasOnly.v1') === '1'; } catch { /* ignore */ }
    return false;
  });
  useEffect(() => {
    try { localStorage.setItem('altY.canvasOnly.v1', canvasOnly ? '1' : '0'); } catch { /* ignore */ }
  }, [canvasOnly]);
  const canvasSurfaceActive = canvasMode || canvasOnly;
  const canvasContent = useMemo(
    () => (canvasSurfaceActive ? findLatestCanvas(chat.messages) : null),
    [canvasSurfaceActive, chat.messages],
  );
  const canvasWriting = useMemo(() => {
    if (!canvasSurfaceActive || !chat.busy) return false;
    const text = latestAssistantText(chat.messages);
    return formatAssistantCanvasChat(text, { canvasMode: true }).writingCanvas;
  }, [canvasSurfaceActive, chat.busy, chat.messages]);
  const [canvasBadge, setCanvasBadge] = useState<WorkspaceCanvasBadge>(null);
  const canvasWasWritingRef = useRef(false);
  const canvasDoneTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!canvasSurfaceActive) {
      canvasWasWritingRef.current = false;
      setCanvasBadge(null);
      return;
    }
    if (canvasWriting) {
      canvasWasWritingRef.current = true;
      if (canvasDoneTimerRef.current != null) {
        window.clearTimeout(canvasDoneTimerRef.current);
        canvasDoneTimerRef.current = null;
      }
      setCanvasBadge('writing');
      return;
    }
    if (canvasWasWritingRef.current && canvasContent) {
      canvasWasWritingRef.current = false;
      setCanvasBadge('done');
      if (canvasDoneTimerRef.current != null) window.clearTimeout(canvasDoneTimerRef.current);
      canvasDoneTimerRef.current = window.setTimeout(() => {
        canvasDoneTimerRef.current = null;
        setCanvasBadge(null);
      }, 2200);
      return;
    }
    canvasWasWritingRef.current = false;
  }, [canvasSurfaceActive, canvasWriting, canvasContent]);
  useEffect(() => () => {
    if (canvasDoneTimerRef.current != null) window.clearTimeout(canvasDoneTimerRef.current);
  }, []);

  // Agent-factory mode emits one of two diagram shapes:
  //  - ProcessMapDoc (`kind: "process-map"`, nodes + edges) — the default,
  //    rendered by the pan/zoom-able ProcessMapCanvas.
  //  - FlowDoc (legacy `steps` spine) — used when the user asks for a "simple"
  //    map; rendered by FactoryCanvas.
  // We probe for a process map first because the parser is stricter; a
  // FlowDoc-shaped reply only takes effect when no process map is present.
  const chatProcessMap = useMemo(
    () => (isFactoryMode ? findLatestProcessMap(chat.messages) : null),
    [isFactoryMode, chat.messages],
  );
  const chatFlow = useMemo(
    () => (isFactoryMode && !chatProcessMap ? findLatestFlow(chat.messages) : null),
    [isFactoryMode, chatProcessMap, chat.messages],
  );
  // AUTO chats stay in `interact` mode but self-route via AUTO_PROMPT; the
  // picker selection ('auto') is the signal. When AUTO emits a diagram inline
  // (it follows the same fenced-```json``` contract as Agent Factory) we render
  // it on the left READ-ONLY — no Make/Pin/Edit, those belong to a real
  // Agent Factory chat. Scoped to NOT-factory / NOT-online-dev so it never
  // competes with those modes' own left-pane content.
  const isAutoMode = selectedAgentId === 'auto' && !isFactoryMode && !isOnlineDevMvpMode;
  const autoProcessMap = useMemo(
    () => (isAutoMode ? findLatestProcessMap(chat.messages) : null),
    [isAutoMode, chat.messages],
  );
  const autoFlow = useMemo(
    () => (isAutoMode && !autoProcessMap ? findLatestFlow(chat.messages) : null),
    [isAutoMode, autoProcessMap, chat.messages],
  );
  // RunId for which the user dismissed the AUTO read-only diagram via X. Mirror
  // of closedDevEnvRunId: re-opening the same chat keeps it hidden; switching
  // chats clears it so the next chat's AUTO diagram can show.
  const [closedAutoDiagramRunId, setClosedAutoDiagramRunId] = useState<number | null>(null);
  useEffect(() => {
    if (closedAutoDiagramRunId != null && chat.activeRunId !== closedAutoDiagramRunId) {
      setClosedAutoDiagramRunId(null);
    }
  }, [chat.activeRunId, closedAutoDiagramRunId]);
  const showAutoDiagram = isAutoMode
    && closedAutoDiagramRunId !== chat.activeRunId
    && !!(autoProcessMap || autoFlow);
  // RunId for which the user closed the agent-factory diagram via X. Same
  // dismiss-per-chat pattern as the AUTO diagram above: hide the diagram for
  // THIS chat (revealing the app iframe / conversation behind it) without
  // switching to a different chat. Cleared when the active chat changes, and
  // when the user sends again (so a fresh diagram re-appears — see
  // handleSendMessage). An explicit Edit gesture (editingAgentId) overrides it.
  const [closedFactoryDiagramRunId, setClosedFactoryDiagramRunId] = useState<number | null>(null);
  useEffect(() => {
    if (closedFactoryDiagramRunId != null && chat.activeRunId !== closedFactoryDiagramRunId) {
      setClosedFactoryDiagramRunId(null);
    }
  }, [chat.activeRunId, closedFactoryDiagramRunId]);
  // Manual-edit overlay for the active process-map chat. The ProcessMapCanvas
  // surfaces drag/resize/text edits via onChange; we hold the latest snapshot
  // here and feed it back to the AI on the next user send (as a primer) so
  // talking to the agent factory after manual tweaks continues from what the
  // user sees, not the AI's stale last reply.
  const [liveProcessMap, setLiveProcessMap] = useState<ProcessMapDoc | null>(null);
  const liveProcessMapRef = useRef<ProcessMapDoc | null>(null);
  useEffect(() => { liveProcessMapRef.current = liveProcessMap; }, [liveProcessMap]);
  // Reset the manual overlay whenever a fresh AI map arrives (new chat, new
  // turn) — the canvas component diffs against the AI doc internally so we
  // don't need to mirror that state here; we just clear our cached snapshot.
  useEffect(() => { setLiveProcessMap(null); }, [chatProcessMap?.title, chatProcessMap?.nodes.length, chat.activeRunId]);
  // Snapshot of the previously-shown diagram, kept visible after the user
  // clicks "New chat" (or switches to a pinned agent's chat) so the canvas
  // doesn't blank out before the next chat's diagram streams in. Paired with
  // frozenFromRunId so we only clear it once the active chat has actually
  // switched — chat.startNewChat() / attachChat() are async, so for one tick
  // chatFlow still resolves against the previous chat's messages.
  const [frozenFlow, setFrozenFlow] = useState<FlowDoc | null>(null);
  const [frozenFromRunId, setFrozenFromRunId] = useState<number | null>(null);
  const factoryFlow = useMemo(() => {
    // Only an explicit edit gesture surfaces the pin's static implementation
    // flow as the initial state. Once the edit chat emits an updated diagram,
    // prefer that chat-owned flow so the canvas reflects the conversation the
    // user is editing instead of pinning itself to stale saved data.
    // Plain pin selection lets chatFlow / frozenFlow drive the canvas, so
    // re-opening an agent-factory chat shows the chat's own diagram.
    if (editingAgentId) {
      return chatFlow ?? allPinnedAgents.find((a) => a.id === editingAgentId)?.flow ?? null;
    }
    if (chatFlow) return chatFlow;
    return frozenFlow;
  }, [editingAgentId, chatFlow, allPinnedAgents, frozenFlow]);
  useEffect(() => {
    if (!frozenFlow) return;
    // Clear the frozen-from-previous-chat diagram once we've moved on. Old
    // condition required chatFlow to arrive first, but a fresh blank chat
    // never emits one — leaving the old diagram visible forever. Triggering
    // on the run id change alone clears it whether the new chat has a flow
    // or not.
    if (chat.activeRunId === frozenFromRunId) return;
    setFrozenFlow(null);
    setFrozenFromRunId(null);
  }, [frozenFlow, chat.activeRunId, frozenFromRunId]);

  // "Make agent" implements the designed diagram end-to-end:
  //   1. Save the agent into localStorage (so the pin appears immediately in
  //      the picker + sidebar without a reload).
  //   2. Dispatch a dev-orchestrator turn via the broker (`op:"make_agent"`)
  //      so the per-agent impl doc gets written, and — for agents that need
  //      net-new tools/configs — any additional platform code lands too.
  //      Mirrors the "Edit agent" flow that ships built-in pin updates.
  //   3. Rename the originating chat after the diagram title so the
  //      conversation is filed under that agent's name.
  // See /opt/onesheet-agent-y/docs/user-agents/README.md for the convention
  // the dev turn follows when writing the impl doc.
  const handleMakeAgent = useCallback(async (flow: FlowDoc) => {
    const title = (flow.title || '').trim();
    if (!title || chat.activeRunId == null) return;
    // Capture the current customer + deployment base URL from bridge context /
    // active tab so the scheduled runner binds to the right OneSheet instance.
    const rawCustomerId = bridgeContext?.customerId;
    const agentCustomerId = rawCustomerId != null ? Number(rawCustomerId) : undefined;
    const agentBaseUrl = activeTab ? tabOrigin(activeTab.url) ?? undefined : undefined;
    // 1. Persist the user agent on the broker (owner-scoped) so it follows
    //    the user across browsers. Optimistic UI: show the pin row right away
    //    on click, reconcile with the server's reply when it lands. Guard
    //    against double-clicks: if the current chat already pinned an agent,
    //    reuse the existing one instead of duplicating.
    const existing = userAgents.find((a) => a.creatorRunId === chat.activeRunId);
    if (!existing) {
      const optimisticId = `user-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      // Register the agent in the picker dropdown but NOT in the sidebar pin
      // list. The diagram's separate Pin button is what flips `pinned` to true.
      const optimistic: PinnedAgent = {
        id: optimisticId,
        label: flow.title || 'Untitled agent',
        icon: 'fa-robot',
        mode: 'interact',
        runnable: true,
        flow,
        pinned: false,
        ...(chat.activeRunId ? { creatorRunId: chat.activeRunId } : {}),
        ...(agentCustomerId != null && !isNaN(agentCustomerId) ? { customerId: agentCustomerId } : {}),
        ...(agentBaseUrl ? { baseUrl: agentBaseUrl } : {}),
      };
      setUserAgents((prev) => [...prev, optimistic]);
      setSelectedPinnedAgentId(optimisticId);
      // Make the new pin the active picker selection too, not just the
      // sidebar highlight — so the user's NEXT send routes through this
      // agent's primer + interact mode instead of staying in design mode
      // (where the broker wraps every prompt in AGENT_FACTORY_PROMPT and
      // every reply is just another diagram).
      setSelectedAgentId(optimisticId);
      void createUserAgent(flow, chat.activeRunId, {
        pinned: false,
        customerId: agentCustomerId != null && !isNaN(agentCustomerId) ? agentCustomerId : undefined,
        baseUrl: agentBaseUrl,
      }).then(
        (saved) => {
          setUserAgents((prev) => prev.map((a) => (a.id === optimisticId ? saved : a)));
          setSelectedPinnedAgentId((current) => (current === optimisticId ? saved.id : current));
          setSelectedAgentId((current) => (current === optimisticId ? saved.id : current));
        },
        () => {
          // Sync failed — drop the optimistic row so the UI matches the server.
          setUserAgents((prev) => prev.filter((a) => a.id !== optimisticId));
          setSelectedPinnedAgentId((current) => (current === optimisticId ? null : current));
          setSelectedAgentId((current) => (current === optimisticId ? 'auto' : current));
        },
      );
    }
    // 2. Kick off the dev-orchestrator turn that writes the impl doc + any
    //    net-new code the agent needs. Pass recent chat context so the dev
    //    agent knows what the user actually asked for, not just the diagram
    //    shape. The broker is busy-aware and will surface a system message
    //    if the user clicks during another turn.
    chat.makeAgent(flow, chat.messages);
    // 3. Rename the originating chat after the diagram title (last so a
    //    rename failure doesn't block the implementation turn). The user
    //    stays on this chat while the dev turn runs — it's busy (Stop
    //    button instead of Send) until the impl turn finishes, after
    //    which the new agent is ready to use right here.
    try {
      await chat.renameChat(chat.activeRunId, title);
    } catch {
      /* non-fatal — the chat is still on the WS and the agent is registered */
    }
  }, [chat, userAgents, bridgeContext, activeTab]);
  // Pin the current diagram into the sidebar pin list. If an agent was
  // already registered via "Make agent" for this chat, flip its `pinned` flag
  // server-side and optimistically locally. Otherwise create a fresh
  // pinned=true agent.
  const handlePinAgent = useCallback((flow: FlowDoc) => {
    const existing = chat.activeRunId != null
      ? userAgents.find((a) => a.creatorRunId === chat.activeRunId)
      : undefined;
    if (existing) {
      setUserAgents((prev) =>
        prev.map((a) => (a.id === existing.id ? { ...a, pinned: true } : a)));
      setSelectedPinnedAgentId(existing.id);
      void updateUserAgent(existing.id, { pinned: true }).then(
        (saved) => setUserAgents((prev) => prev.map((a) => (a.id === existing.id ? saved : a))),
        () => {
          // Server rejected — revert.
          setUserAgents((prev) =>
            prev.map((a) => (a.id === existing.id ? { ...a, pinned: false } : a)));
        },
      );
      return;
    }
    const optimisticId = `user-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const optimistic: PinnedAgent = {
      id: optimisticId,
      label: flow.title || 'Untitled agent',
      icon: 'fa-robot',
      mode: 'interact',
      runnable: true,
      flow,
      pinned: true,
      ...(chat.activeRunId ? { creatorRunId: chat.activeRunId } : {}),
    };
    setUserAgents((prev) => [...prev, optimistic]);
    setSelectedPinnedAgentId(optimisticId);
    void createUserAgent(flow, chat.activeRunId, { pinned: true }).then(
      (saved) => {
        setUserAgents((prev) => prev.map((a) => (a.id === optimisticId ? saved : a)));
        setSelectedPinnedAgentId((current) => (current === optimisticId ? saved.id : current));
      },
      () => {
        setUserAgents((prev) => prev.filter((a) => a.id !== optimisticId));
        setSelectedPinnedAgentId((current) => (current === optimisticId ? null : current));
      },
    );
  }, [chat.activeRunId, userAgents]);
  const alreadyPinned = useMemo(
    () => isFactoryMode && chat.activeRunId != null
      && userAgents.some((a) => a.creatorRunId === chat.activeRunId && a.pinned !== false),
    [isFactoryMode, chat.activeRunId, userAgents],
  );
  // Plain click on a pin: pure selection. Updates the picker's `selectedAgentId`
  // so the NEXT send uses this agent's primer / mode (via handleSendMessage
  // below). Crucially, does NOT switch modes, start a new chat, or surface the
  // FactoryCanvas — editing the agent is now an explicit gesture
  // (handleEditPinnedAgent).
  const handleSelectPinnedAgent = useCallback((id: string | null) => {
    setSelectedUserIds([]);
    if (!id || id === 'auto') {
      setSelectedAgentId('auto');
      return;
    }
    setSelectedAgentId(id);
  }, []);

  // Multi-select recipient swap. Any non-empty selection puts the chat in
  // user-to-user mode (selectedAgentId cleared); going back to zero users
  // restores the default 'auto' agent so the next send routes itself.
  const handleSelectUsers = useCallback((userIds: string[]) => {
    setSelectedUserIds(userIds);
    if (userIds.length > 0) {
      setSelectedAgentId('');
    } else {
      setSelectedAgentId('auto');
    }
  }, []);

  // Explicit "edit this agent" gesture — formerly the body of clicking a pin.
  // Switches into agent-factory mode, reattaches to (or mints) the agent's
  // edit chat, and surfaces the FactoryCanvas with the flow loaded. Setting
  // `editingAgentId` is what gates FactoryCanvas visibility from now on.
  const handleEditPinnedAgent = useCallback((id: string) => {
    const agent = allPinnedAgents.find((a) => a.id === id);
    if (!agent) return;
    setEditingAgentId(id);
    setSelectedPinnedAgentId(id);
    setSelectedAgentId('agent-factory');
    setClosedFactoryDiagramRunId(null);
    setMobileView('chat');

    if (EDITABLE_BUILTIN_PIN_IDS.has(agent.id)) {
      setSelectedPinnedAgentId(null);
      setFrozenFlow(agent.flow);
      setFrozenFromRunId(chat.activeRunId ?? null);
      const storedRunId = getBuiltinEditChatId(agent.id);
      const storedRunKnown = storedRunId != null
        && (storedRunId === chat.activeRunId || chat.chats.some((c) => c.runId === storedRunId));
      void (async () => {
        if (chat.mode !== 'agent-factory') {
          await chat.switchMode('agent-factory');
        }
        if (storedRunId && storedRunKnown && (storedRunId !== chat.activeRunId || !chat.connected)) {
          const attachedRunId = await chat.attachChat(storedRunId);
          if (attachedRunId != null) {
            chat.preserveEmptyChat(attachedRunId);
            chat.switchChatMode('agent-factory');
            chat.setPendingPrimer(formatPinEditPrimer(agent));
            setBuiltinEditChatId(agent.id, attachedRunId);
            setBuiltinEditRev((rev) => rev + 1);
            return;
          }
          setBuiltinEditChatId(agent.id, null);
          setBuiltinEditRev((rev) => rev + 1);
        } else if (storedRunId && storedRunKnown) {
          chat.switchChatMode('agent-factory');
          chat.setPendingPrimer(formatPinEditPrimer(agent));
          return;
        } else if (storedRunId && !storedRunKnown) {
          setBuiltinEditChatId(agent.id, null);
          setBuiltinEditRev((rev) => rev + 1);
        }
        chat.setPendingPrimer(formatPinEditPrimer(agent));
        const newRunId = await chat.startNewChat();
        if (newRunId != null) {
          chat.preserveEmptyChat(newRunId);
          chat.switchChatMode('agent-factory');
          void chat.renameChat(newRunId, `Edit ${agent.label}`);
          setBuiltinEditChatId(agent.id, newRunId);
          setBuiltinEditRev((rev) => rev + 1);
        }
      })();
      return;
    }

    // User pin — reattach to its creator chat (the agent-factory chat where
    // the diagram was designed) so the user can iterate on it. Fall back to a
    // fresh chat primed with the diagram if the original is gone.
    let targetRunId = agent.creatorRunId ?? null;
    if (targetRunId && targetRunId !== chat.activeRunId && !chat.chats.some((c) => c.runId === targetRunId)) {
      targetRunId = null;
    }
    if (!targetRunId) {
      const label = (agent.label || agent.flow.title || '').trim();
      const titleMatch = label
        ? chat.chats.find((c) => (c.title || '').trim() === label)
        : null;
      if (titleMatch) {
        targetRunId = titleMatch.runId;
        if (agent.id.startsWith('user-')) {
          // Persist the backfilled creatorRunId on the server, then reflect
          // it locally. Optimistic update first so the in-memory state lines
          // up immediately; the PUT reply is the source of truth on landing.
          setUserAgents((prev) => prev.map((a) =>
            a.id === agent.id ? { ...a, creatorRunId: targetRunId ?? undefined } : a));
          void updateUserAgent(agent.id, { creatorRunId: targetRunId }).then(
            (saved) => setUserAgents((prev) => prev.map((a) => (a.id === agent.id ? saved : a))),
            () => { /* non-fatal — local state still has the binding for this session */ },
          );
        }
      }
    }
    const persistCreatorRunId = (runId: number) => {
      if (!agent.id.startsWith('user-')) return;
      setUserAgents((prev) => prev.map((a) =>
        a.id === agent.id ? { ...a, creatorRunId: runId } : a));
      void updateUserAgent(agent.id, { creatorRunId: runId }).then(
        (saved) => setUserAgents((prev) => prev.map((a) => (a.id === agent.id ? saved : a))),
        () => { /* non-fatal — this session still knows the fallback chat */ },
      );
    };

    const startReplacementEditChat = async () => {
      chat.setPendingPrimer(formatPinEditPrimer(agent));
      const newRunId = await chat.startNewChat();
      if (newRunId != null) {
        chat.preserveEmptyChat(newRunId);
        chat.switchChatMode('agent-factory');
        void chat.renameChat(newRunId, `Edit ${agent.label || agent.flow.title || 'agent'}`);
        persistCreatorRunId(newRunId);
        setSelectedPinnedAgentId(id);
      }
      return newRunId;
    };

    void (async () => {
      if (chat.mode !== 'agent-factory') {
        await chat.switchMode('agent-factory');
      }
      if (targetRunId && (targetRunId !== chat.activeRunId || !chat.connected)) {
        setFrozenFlow(agent.flow);
        setFrozenFromRunId(chat.activeRunId ?? null);
        setSelectedPinnedAgentId(null);
        const attachedRunId = await chat.attachChat(targetRunId);
        if (attachedRunId != null) {
          chat.preserveEmptyChat(attachedRunId);
          chat.switchChatMode('agent-factory');
          chat.setPendingPrimer(formatPinEditPrimer(agent));
          setSelectedPinnedAgentId(id);
          return;
        }
        await startReplacementEditChat();
      } else if (!targetRunId) {
        await startReplacementEditChat();
      } else {
        chat.switchChatMode('agent-factory');
        chat.setPendingPrimer(formatPinEditPrimer(agent));
        setSelectedPinnedAgentId(id);
      }
    })();
  }, [allPinnedAgents, chat]);
  const handleDeletePinnedAgent = useCallback((id: string) => {
    const agent = userAgents.find((a) => a.id === id);
    if (!agent) return;
    if (!window.confirm(`Delete pinned agent?\n\n${agent.label}`)) return;
    // Optimistic remove; if the server delete fails (e.g. network blip) we
    // re-list to pick the row back up next reload.
    setUserAgents((prev) => prev.filter((a) => a.id !== id));
    setSelectedPinnedAgentId((current) => (current === id ? null : current));
    void deleteUserAgent(id).catch(() => {
      void listUserAgents().then((server) => setUserAgents(server)).catch(() => undefined);
    });
  }, [userAgents]);
  const handleStartNewChat = useCallback(() => {
    if (isFactoryMode) {
      setFrozenFlow(factoryFlow);
      setFrozenFromRunId(chat.activeRunId ?? null);
    }
    setSelectedPinnedAgentId(null);
    setEditingAgentId(null);
    // A "new chat" gesture should always start with a clean slate — clear any
    // primer left over from a pin-edit / AUTO-route flow whose chat never got
    // its first send (e.g. user clicked Edit on a pin to switch into the
    // editor chat, then immediately clicked "+ New agent" to design something
    // else; without this the new chat's first send carries the edit primer).
    chat.setPendingPrimer(null);
    // If a non-AUTO pin is currently selected and its mode differs from the
    // panel's current mode, switch into the pin's mode first so the new chat
    // is born in the right list (agent-factory / alty-dev / interact).
    const picked = selectedAgentId !== 'auto'
      ? allPinnedAgents.find((a) => a.id === selectedAgentId)
      : null;
    if (picked && picked.mode !== chat.mode) {
      void (async () => {
        await chat.switchMode(picked.mode);
        await chat.startNewChat();
      })();
      return;
    }
    chat.startNewChat();
  }, [allPinnedAgents, chat, factoryFlow, isFactoryMode, selectedAgentId]);
  const handleStartSubchat = useCallback(async () => {
    const rootRunId = bmadWorkflowRunId ?? chat.activeRunId;
    if (isBmadAgentActive && bmadWorkflow && rootRunId != null) {
      setBmadWorkflowSnapshot({ runId: rootRunId, agentId: selectedAgentId, workflow: bmadWorkflow });
      setBmadChildRootHint(rootRunId);
      setBmadView(true);
    }
    return await chat.startSubchat();
  }, [bmadWorkflowRunId, chat, isBmadAgentActive, bmadWorkflow, selectedAgentId]);

  const [agentEditPending, setAgentEditPending] = useState<
    { runId: number; agentId: string; sinceIndex: number } | null
  >(null);
  const agentEditSawBusyRef = useRef(false);

  // AUTO routing: when 'auto' is the selected agent we no longer make a
  // separate routing call. The send is flagged `auto` and the broker wraps the
  // turn with AUTO_PROMPT (every agent's directions inline + "pick the best fit
  // and just do it"), so the working turn self-routes. Explicit pin selections
  // and agent-factory design still take their dedicated branches below.
  const handleSendMessage = useCallback(
    (text: string, effort: string = 'high', images: AltYImageAttachment[] = []) => {
      // Clear any stale AUTO flag up front so only the AUTO branch below can set
      // it — a flag left pending by an AUTO send that got queued while busy must
      // not contaminate a later explicit-pin / design send. (A genuinely queued
      // AUTO turn still keeps the flag: its replay calls the raw sendMessage,
      // not this wrapper, so this reset doesn't run for it.)
      chat.setPendingAuto(false);
      // Canvas mode: no env tab is selected, so the left pane is the workspace
      // canvas. Each turn carries a primer telling the AI it has that surface
      // and how to render onto it; if a branch below sets its own primer we
      // append the canvas hint to it instead of overwriting.
      const inCanvasMode = canvasMode || canvasOnly;
      const composePrimer = (p: string | null): string | null => {
        if (!inCanvasMode) return p;
        const c = canvasModePrimer(canvasOnly ? 'canvas-only' : 'empty');
        return p ? `${p}\n\n${c}` : c;
      };
      // Idea Agent: capturing the typed message into the idea list runs
      // alongside the normal turn (the agent still replies). The broker dedupes
      // and we toast the outcome. Capture only the user's words, not the primer.
      if (isIdeaAgentActive) captureIdea(text);
      // A new send means the user wants to keep working — un-dismiss any
      // closed agent-factory diagram so the turn's fresh diagram shows.
      setClosedFactoryDiagramRunId(null);
      // Explicit pin selection wins over "I'm designing right now". If the
      // user picked a runnable interact-mode pin from the picker but the chat
      // is still in agent-factory mode (e.g. they just clicked Make agent and
      // immediately typed a kickoff in the design chat), we want to honour
      // the picked pin — not keep designing diagrams.
      const explicitPick = selectedAgentId !== 'auto'
        ? allPinnedAgents.find((a) => a.id === selectedAgentId) ?? null
        : null;
      const agentFactoryPick = explicitPick?.id === 'agent-factory';
      const editedAgent = editingAgentId
        ? allPinnedAgents.find((a) => a.id === editingAgentId) ?? null
        : null;
      if (editingAgentId && chat.mode !== 'agent-factory') {
        chat.switchChatMode('agent-factory');
      }
      // In agent-factory mode with no explicit pin, the user is designing —
      // skip the router so AUTO doesn't yank them into an existing pin's
      // flow when their description happens to keyword-match one.
      // An explicit edit gesture is also an Agent Factory conversation, even
      // when the edited pin itself is runnable in interact mode.
      if ((editingAgentId || chat.mode === 'agent-factory') && (editingAgentId || !explicitPick || agentFactoryPick)) {
        // If the user has manually edited a process map since the AI's last
        // emit, ship the current state as a primer so "make the diamond
        // bigger" iterates on what's on screen rather than the AI's stale
        // last reply. We only do this when there IS a live overlay — fresh
        // turns with no manual edits skip the noise.
        const primerParts: string[] = [];
        if (editedAgent) {
          primerParts.push(formatPinEditPrimer({
            ...editedAgent,
            flow: factoryFlow ?? editedAgent.flow,
          }));
        }
        const live = liveProcessMapRef.current;
        if (live) {
          primerParts.push(
            'The user manually edited the current process map. Treat the JSON ' +
            'below as the latest state and apply their next request to it. ' +
            'Emit the WHOLE updated process-map JSON as the reply.\n\n' +
            '```json\n' + serializeProcessMap(live) + '\n```',
          );
        }
        const composedFactory = composePrimer(primerParts.length ? primerParts.join('\n\n') : null);
        if (composedFactory) chat.setPendingPrimer(composedFactory);
        if (editingAgentId && chat.activeRunId != null && userAgents.some((a) => a.id === editingAgentId)) {
          agentEditSawBusyRef.current = false;
          setAgentEditPending({
            runId: chat.activeRunId,
            agentId: editingAgentId,
            sinceIndex: chat.messages.length,
          });
        }
        chat.sendMessage(text, effort, images);
        return;
      }
      // Sending a message dismisses any pinned-agent edit view — the canvas
      // is for designing, the iframes are for working.
      setEditingAgentId(null);
      if (explicitPick) {
        const picked = explicitPick;
        // Pin's mode differs from the current chat mode. When the target mode
        // shares this chat's workspace (interact / agent-factory / bmad) and a
        // live chat is open, switch the agent IN PLACE so the conversation
        // continues — that's the "switch agents mid-chat" the user wants.
        // Only the dev-orchestrator modes (or a panel with no chat yet) fork a
        // fresh chat, because those own a dedicated worktree / dev fork.
        if (picked.mode !== chat.mode) {
          const flowPrimer = picked.runnable && picked.flow?.steps?.length
            ? formatFlowAsPrimer(picked)
            : null;
          if (canSwitchAgentInPlace(chat.mode, picked.mode) && chat.activeRunId != null) {
            chat.switchChatMode(picked.mode);
            const composedPick = composePrimer(flowPrimer);
            if (composedPick) chat.setPendingPrimer(composedPick);
            chat.sendMessage(text, effort, images);
            return;
          }
          void (async () => {
            await chat.switchMode(picked.mode);
            await chat.startNewChat();
            const composedPick = composePrimer(flowPrimer);
            if (composedPick) chat.setPendingPrimer(composedPick);
            chat.sendMessage(text, effort, images);
          })();
          return;
        }
        // For non-AUTO pin selections, prime the message with the agent's
        // flow so the next send carries the picked agent's instructions
        // through to the assistant (the previous behaviour relied on
        // clicking the pin to do this; selection now does it implicitly).
        const pickedPrimer = picked.runnable && picked.flow?.steps?.length
          ? formatFlowAsPrimer(picked)
          : null;
        const composedSame = composePrimer(pickedPrimer);
        if (composedSame) chat.setPendingPrimer(composedSame);
        chat.sendMessage(text, effort, images);
        return;
      }
      // AUTO: no pre-routing LLM call any more. Flag the send as `auto` and the
      // broker wraps this turn with AUTO_PROMPT — every agent's directions
      // inline plus "pick the best fit for this prompt and just do it". The
      // turn self-routes and runs in the current workspace (interact by
      // default), so there's no mode switch / fork here.
      chat.setPendingAuto(true);
      const composedAuto = composePrimer(null);
      if (composedAuto) chat.setPendingPrimer(composedAuto);
      chat.sendMessage(text, effort, images);
    },
    [allPinnedAgents, chat, selectedAgentId, isIdeaAgentActive, captureIdea, editingAgentId, factoryFlow, userAgents, canvasMode, canvasOnly],
  );

  useEffect(() => {
    const pending = agentEditPending;
    if (!pending) return;
    if (chat.activeRunId !== pending.runId) {
      agentEditSawBusyRef.current = false;
      setAgentEditPending(null);
      return;
    }
    if (chat.busy) {
      agentEditSawBusyRef.current = true;
      return;
    }
    if (!agentEditSawBusyRef.current) return;
    agentEditSawBusyRef.current = false;
    setAgentEditPending(null);
    const agent = userAgents.find((a) => a.id === pending.agentId);
    if (!agent) return;
    const since = chat.messages.slice(pending.sinceIndex);
    const pm = findLatestProcessMap(since);
    const editedFlow = pm ? processMapToFlow(pm) : findLatestFlow(since);
    if (!editedFlow) {
      chat.addMeta('edit: agent left unchanged — the reply had no updated diagram');
      return;
    }
    if (flowEquals(agent.flow, editedFlow)) {
      chat.addMeta('edit: no diagram change to save');
      return;
    }
    const nextLabel = editedFlow.title?.trim() || agent.label;
    const nextAgent = { ...agent, flow: editedFlow, label: nextLabel };
    setUserAgents((prev) => prev.map((a) => (a.id === agent.id ? nextAgent : a)));
    void updateUserAgent(agent.id, { flow: editedFlow, label: nextLabel }).then(
      (saved) => {
        setUserAgents((prev) => prev.map((a) => (a.id === agent.id ? saved : a)));
        chat.addMeta(`saved edits to “${saved.label}”`);
      },
      () => {
        setUserAgents((prev) => prev.map((a) => (a.id === agent.id ? agent : a)));
        chat.addMeta('edit: failed to save the updated agent');
      },
    );
  }, [agentEditPending, chat.activeRunId, chat.busy, chat.messages, chat.addMeta, userAgents]);

  // Selecting an existing chat row should snap the picker to whatever agent
  // that chat was using before — same agent as the icon shown on the chat row.
  // The chat's mode (server-persisted) maps 1:1 to the built-in pin icons, and
  // user pins point at their creator chat via `creatorRunId`. Wrapping
  // attachChat (rather than reacting to activeRunId changes) keeps "+ New chat"
  // / mode-switch flows from clobbering the picker.
  const attachChatRaw = chat.attachChat;
  const handleAttachChat = useCallback(async (runId: number) => {
    // If this chat has a remembered agent + model (it was used before), the
    // panel restores BOTH on attach (see chatSelection / AltYPanel's restore
    // effect) — so don't derive an agent from its mode here, or the picker
    // would flash the mode default before snapping to what the chat actually
    // used. Only chats with no memory (started before this existed) fall back
    // to the mode-derived agent so the picker still lines up with the mode.
    if (!hasChatSelection(runId)) {
      const userPin = allPinnedAgents.find((a) => a.id.startsWith('user-') && a.creatorRunId === runId);
      if (userPin) {
        setSelectedAgentId(userPin.id);
      } else {
        const summary = chat.chats.find((c) => c.runId === runId);
        if (summary) {
          const pinId = summary.mode === 'agent-factory' ? 'agent-factory'
            : summary.mode === 'alty-dev' ? 'alty-dev'
            : summary.mode === 'online-dev-mvp' ? 'online-dev-mvp'
            : summary.mode === 'bmad' ? 'bmad'
            : summary.mode === 'chat' ? 'chat'
            : summary.mode === 'scope-tasks' ? 'scope-tasks'
            : 'interact';
          const currentPick = allPinnedAgents.find((a) => a.id === selectedAgentId) ?? null;
          const targetRoot = summary.rootRunId ?? summary.parentRunId ?? summary.runId;
          const currentRoot = activeChatSummary?.rootRunId
            ?? activeChatSummary?.parentRunId
            ?? chat.activeRunId
            ?? null;
          const keepCurrentInteractAgent = summary.mode === 'interact'
            && currentPick?.mode === 'interact'
            && currentPick.id !== 'interact'
            && targetRoot != null
            && currentRoot === targetRoot;
          if (!keepCurrentInteractAgent) setSelectedAgentId(pinId);
        }
      }
    }
    return await attachChatRaw(runId);
  }, [allPinnedAgents, attachChatRaw, chat.chats, selectedAgentId, activeChatSummary, chat.activeRunId]);

  // Wrap the chat hook so the panel's `chat.sendMessage` call funnels through
  // the AUTO router and `chat.attachChat` restores the chat's prior agent.
  // Everything else on `chat` passes through untouched — intricate
  // streaming/replay refs in useAltYChat must not be cloned.
  const chatWithAutoRouting = useMemo(
    () => ({ ...chat, sendMessage: handleSendMessage, attachChat: handleAttachChat }),
    [chat, handleSendMessage, handleAttachChat],
  );

  // What the sidebar should highlight as "the pinned agent currently being
  // viewed". Falls back to:
  //   • the pin whose creator chat is attached (user agents), or
  //   • the built-in pin whose editor chat is attached (e.g. Agent Factory)
  // so the pin row stays lit while you're chatting with its diagram.
  const visiblePinnedAgentId = useMemo(() => {
    if (selectedPinnedAgentId) return selectedPinnedAgentId;
    if (selectedAgentId && selectedAgentId !== 'auto') return selectedAgentId;
    if (isFactoryMode && chat.activeRunId != null) {
      const byCreator = allPinnedAgents.find((a) => a.creatorRunId === chat.activeRunId);
      if (byCreator) return byCreator.id;
      for (const pinId of EDITABLE_BUILTIN_PIN_IDS) {
        if (getBuiltinEditChatId(pinId) === chat.activeRunId) return pinId;
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPinnedAgentId, selectedAgentId, isFactoryMode, chat.activeRunId, allPinnedAgents, builtinEditRev]);

  // The built-in pin (if any) whose editor chat we're currently in. Drives
  // the FactoryCanvas's "Edit agent" affordance: when set, the canvas shows
  // a modified-from-implementation indicator + Edit-agent button instead of
  // the Make-agent / Pin buttons that apply to fresh user-built agents.
  const editingBuiltinPin = useMemo(() => {
    if (!isFactoryMode || chat.activeRunId == null) return null;
    for (const pinId of EDITABLE_BUILTIN_PIN_IDS) {
      if (getBuiltinEditChatId(pinId) === chat.activeRunId) {
        return allPinnedAgents.find((a) => a.id === pinId) ?? null;
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFactoryMode, chat.activeRunId, allPinnedAgents, builtinEditRev]);

  const isDiagramModified = useMemo(() => {
    if (!editingBuiltinPin || !chatFlow) return false;
    return !flowEquals(chatFlow, editingBuiltinPin.flow);
  }, [editingBuiltinPin, chatFlow]);

  const handleEditAgent = useCallback((flow: FlowDoc) => {
    if (!editingBuiltinPin) return;
    chat.editPinnedAgent(editingBuiltinPin.id, flow, editingBuiltinPin.flow);
  }, [editingBuiltinPin, chat]);

  // Pin/unpin a built-in (persist client-side + re-render so the sidebar
  // updates immediately).
  const toggleBuiltinPin = useCallback((id: string, pinned: boolean) => {
    setBuiltinPinned(id, pinned);
    setPinnedBuiltins(getPinnedBuiltinIds());
  }, []);

  // The built-in pin whose own diagram the FactoryCanvas is currently showing,
  // if any — gates the Pin/Unpin button above the diagram. Only set when the
  // user is viewing/editing a built-in's flow (not while designing a fresh one).
  const viewedBuiltinId = useMemo(() => {
    const candidate = editingBuiltinPin?.id || editingAgentId;
    return candidate && PINNED_AGENTS.some((p) => p.id === candidate) ? candidate : null;
  }, [editingBuiltinPin, editingAgentId]);

  // The agent whose runner metadata feeds the FactoryCanvas's start/stop row.
  // Prefer the agent the user is explicitly editing; fall back to the agent
  // highlighted in the picker / pin sidebar. Built-in pins don't have runner
  // fields so we only look these up on user agents.
  const runMetadataAgent = useMemo(() => {
    const targetId = editingAgentId || visiblePinnedAgentId;
    if (!targetId) return null;
    return userAgents.find((a) => a.id === targetId) ?? null;
  }, [editingAgentId, visiblePinnedAgentId, userAgents]);

  const handleToggleRunning = useCallback(
    (enabled: boolean) => {
      if (!runMetadataAgent) return;
      const id = runMetadataAgent.id;
      // Optimistic update — flip the flag locally so the button reflects the
      // new state immediately. Reconcile with the server's reply on landing.
      setUserAgents((prev) => prev.map((a) => (a.id === id ? { ...a, enabled } : a)));
      void updateUserAgent(id, { enabled }).then(
        (saved) => setUserAgents((prev) => prev.map((a) => (a.id === id ? saved : a))),
        () => {
          // Server rejected — revert. The next poll will reconcile anyway.
          setUserAgents((prev) =>
            prev.map((a) => (a.id === id ? { ...a, enabled: !enabled } : a)));
        },
      );
    },
    [runMetadataAgent],
  );

  // The user-created agent the current chat is running as. The cog menu's
  // "Improve agent" action targets this: it reflects on the chat against the
  // agent's own diagram and rewrites the agent's flow. Only user agents qualify
  // — built-in pins are revised through Agent Factory's "Edit agent" instead,
  // and AUTO / plain interact / chat have no specific agent to improve.
  const improveAgentTarget = useMemo(() => {
    const id = visiblePinnedAgentId
      || (selectedAgentId !== 'auto' ? selectedAgentId : null);
    if (!id) return null;
    const agent = userAgents.find((a) => a.id === id);
    return agent && agent.runnable ? agent : null;
  }, [visiblePinnedAgentId, selectedAgentId, userAgents]);

  // "Improve agent" dispatch + apply. We send the reflection turn, snapshot the
  // message count, and once the turn has actually run (busy went true → false)
  // adopt the improved FlowDoc the reply emitted (after the snapshot) onto the
  // user agent. Switching chats mid-run abandons the pending improve.
  const [improvePending, setImprovePending] = useState<
    { runId: number; agentId: string; sinceIndex: number } | null
  >(null);
  const improveSawBusyRef = useRef(false);
  const handleImproveAgent = useCallback(() => {
    const agent = improveAgentTarget;
    if (!agent || chat.activeRunId == null) return;
    improveSawBusyRef.current = false;
    if (chat.improveAgent(agent.label, agent.flow)) {
      setImprovePending({ runId: chat.activeRunId, agentId: agent.id, sinceIndex: chat.messages.length });
    }
  }, [improveAgentTarget, chat]);
  useEffect(() => {
    const pending = improvePending;
    if (!pending) return;
    if (chat.activeRunId !== pending.runId) {
      // User navigated to a different chat — abandon this improve.
      improveSawBusyRef.current = false;
      setImprovePending(null);
      return;
    }
    if (chat.busy) {
      improveSawBusyRef.current = true;
      return;
    }
    if (!improveSawBusyRef.current) return; // turn hasn't started yet
    // The improve turn finished — adopt the flow it emitted after dispatch.
    improveSawBusyRef.current = false;
    setImprovePending(null);
    const agent = userAgents.find((a) => a.id === pending.agentId);
    if (!agent) return;
    const since = chat.messages.slice(pending.sinceIndex);
    const pm = findLatestProcessMap(since);
    const improvedFlow = pm ? processMapToFlow(pm) : findLatestFlow(since);
    if (!improvedFlow) {
      chat.addMeta('improve: agent left unchanged — the reply had no updated diagram');
      return;
    }
    if (flowEquals(agent.flow, improvedFlow)) {
      chat.addMeta('improve: no change — the agent was already fit for this task');
      return;
    }
    const nextLabel = improvedFlow.title?.trim() || agent.label;
    setUserAgents((prev) => prev.map((a) => (a.id === agent.id ? { ...a, flow: improvedFlow, label: nextLabel } : a)));
    void updateUserAgent(agent.id, { flow: improvedFlow, label: nextLabel }).then(
      (saved) => {
        setUserAgents((prev) => prev.map((a) => (a.id === agent.id ? saved : a)));
        chat.addMeta(`improved “${saved.label}” — its diagram now reflects this chat`);
      },
      () => {
        setUserAgents((prev) => prev.map((a) => (a.id === agent.id ? agent : a)));
        chat.addMeta('improve: failed to save the updated agent');
      },
    );
  }, [improvePending, chat.busy, chat.activeRunId, chat.messages, chat.addMeta, userAgents]);

  // Close the diagram canvas: drop the explicit edit gesture and dismiss the
  // diagram for the CURRENT chat, revealing the app iframe (desktop) or the
  // conversation (mobile) behind it. Stays on the same chat — closing the
  // diagram is not a request to switch chats. The dismissal clears when the
  // user switches chats (effect above) or sends again (handleSendMessage), so
  // a fresh diagram re-appears. Shared by the desktop canvas + mobile overlay.
  const closeDiagram = useCallback(() => {
    setEditingAgentId(null);
    setMobileView('chat');
    if (chat.activeRunId != null) setClosedFactoryDiagramRunId(chat.activeRunId);
  }, [chat.activeRunId]);

  // Reopen a closed agent-factory diagram for the current chat — the inverse of
  // closeDiagram. Just clears the per-chat dismissal so `diagramCanvas`
  // resurfaces. On mobile this is wired to a header button that replaces the
  // "view online app" globe while a diagram is closed (see AltYPanel).
  const reopenDiagram = useCallback(() => {
    setClosedFactoryDiagramRunId(null);
  }, []);

  // Poll the broker for fresh runner status while the canvas is visible. The
  // canvas is shown whenever `editingAgentId` is set OR the chat is in
  // agent-factory mode with a flow to render. We piggyback on that condition
  // so polling stops as soon as the user closes the diagram.
  const canvasVisible = !!((editingAgentId || isFactoryMode) && factoryFlow);
  useEffect(() => {
    if (!canvasVisible || !authed) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const list = await listUserAgents();
        if (!cancelled) setUserAgents(list);
      } catch {
        /* network blip — next tick retries */
      }
    };
    const id = window.setInterval(tick, 10_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [canvasVisible, authed]);

  useEffect(() => {
    setAuthed(bootstrap.authenticated);
  }, [bootstrap.authenticated]);

  const handleSignedIn = useCallback(() => {
    setAuthed(null);
    void reloadBootstrap().then((nextBootstrap) => {
      setAuthed(nextBootstrap.authenticated);
    }, () => {
      setAuthed(false);
    });
  }, [reloadBootstrap]);

  // Load the user's pinned agents from the broker once we're authed. Runs the
  // one-time localStorage→server migration first (idempotent upsert on the
  // server, so retrying after a partial failure is safe), then lists.
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    void (async () => {
      try { await migrateLegacyUserAgents(); } catch { /* non-fatal — keep going */ }
      try {
        const list = await listUserAgents();
        if (!cancelled) setUserAgents(list);
      } catch {
        // Broker unavailable — leave the array empty rather than fall back to
        // a stale localStorage view. The next reload retries.
      }
    })();
    return () => { cancelled = true; };
  }, [authed]);

  const refreshUserAgents = useCallback(async () => {
    if (!authed) return;
    try {
      setUserAgents(await listUserAgents());
    } catch {
      /* broker may still be restarting; callers can retry later */
    }
  }, [authed]);

  useEffect(() => {
    window.AltY?.registerAppFrame?.(activeFrame);
  }, [activeFrame]);

  // Alt+Y toggles the panel; Escape is swallowed so the legacy alt-y.js handler
  // doesn't close the panel. Capture phase + window-level so we run before the
  // document-level handler in alt-y.js.
  useEffect(() => {
    function onKeydown(e: KeyboardEvent) {
      if (e.altKey && !e.ctrlKey && !e.shiftKey && !e.metaKey && e.key.toLowerCase() === 'y') {
        const t = e.target as HTMLElement | null;
        const editable = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || (t as HTMLElement).isContentEditable);
        if (editable) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        window.AltY?.toggle?.();
        return;
      }
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
      }
    }
    window.addEventListener('keydown', onKeydown, true);
    return () => window.removeEventListener('keydown', onKeydown, true);
  }, []);

  // Open the panel by default once the user is signed in.
  useEffect(() => {
    if (authed) window.AltY?.open?.();
  }, [authed]);

  useEffect(() => {
    setActiveFrame(frameRefs.current.get(activeTabId) ?? null);
  }, [activeTabId, loadedTabIds]);

  const setFrameRef = useCallback(
    (tabId: string) => (el: HTMLIFrameElement | null) => {
      frameRefs.current.set(tabId, el);
      if (tabId === activeTabId) setActiveFrame(el);
    },
    [activeTabId],
  );

  // Workspace canvas close handler — defined up here (before the auth-gate
  // early returns) so the hook order stays stable across renders. Moving it
  // below the `if (!authed)` returns caused "Rendered more hooks than during
  // the previous render" the moment auth flipped from null → true.
  const reopenFirstEnvTab = useCallback(() => {
    const first = config.tabs[0];
    if (!first) return;
    setLoadedTabIds((prev) => {
      if (prev.has(first.id)) return prev;
      const next = new Set(prev);
      next.add(first.id);
      return next;
    });
    setActiveTabId(first.id);
  }, [config.tabs]);

  if (authed === null) {
    return (
      <div className="alty-login">
        <div className="alty-login-card"><div className="alty-login-brand">Alt-Y</div><p>Loading…</p></div>
      </div>
    );
  }
  if (!authed) {
    return <Login onSignedIn={handleSignedIn} />;
  }

  function selectTab(tab: AltYEnvironmentTab) {
    // Toggle: clicking the currently-active env tab deselects it AND unmounts
    // its iframe, so the browser stops spending memory on a server the user
    // isn't viewing. With no env active the left pane falls back to the
    // workspace canvas (rendered in the default slot below).
    if (tab.id === activeTabId) {
      setActiveTabId('');
      setLoadedTabIds((prev) => {
        if (!prev.has(tab.id)) return prev;
        const next = new Set(prev);
        next.delete(tab.id);
        return next;
      });
      frameRefs.current.delete(tab.id);
      return;
    }
    setLoadedTabIds((prev) => {
      if (prev.has(tab.id)) return prev;
      const next = new Set(prev);
      next.add(tab.id);
      return next;
    });
    setActiveTabId(tab.id);
  }

  function handleFrameLoad(tabId: string) {
    const frame = frameRefs.current.get(tabId) ?? null;
    if (tabId === activeTabId) setActiveFrame(frame);
    const resolve = frameLoadResolversRef.current.get(tabId);
    if (resolve) resolve(frame);
  }

  // The Agent Factory / Edit-agent diagram. Computed once and shared by the
  // desktop shell (where it sits behind the panel) and the mobile overlay (a
  // full-screen layer the edit pen pulls up). Null when no diagram applies, or
  // when the user closed it for this chat (closedFactoryDiagramRunId) — an
  // explicit Edit gesture (editingAgentId) re-surfaces it regardless.
  const factoryDiagramDismissed = !editingAgentId && closedFactoryDiagramRunId === chat.activeRunId;
  // Mobile: when an agent-factory diagram has been closed for this chat but one
  // still exists to show, the chat header's "view online app" globe is swapped
  // for a "reopen diagram" button (see AltYPanel). Reopening just un-dismisses.
  const canReopenFactoryDiagram = isFactoryMode && factoryDiagramDismissed && !!(chatProcessMap || factoryFlow);
  const diagramCanvas = factoryDiagramDismissed ? null : isFactoryMode && chatProcessMap ? (
    <ProcessMapCanvas
      doc={chatProcessMap}
      onChange={setLiveProcessMap}
      onMakeAgent={(doc) => handleMakeAgent(processMapToFlow(doc))}
      onPinAgent={(doc) => handlePinAgent(processMapToFlow(doc))}
      canMakeAgent={isFactoryMode && !selectedPinnedAgentId && !editingBuiltinPin}
      canPinAgent={isFactoryMode && !selectedPinnedAgentId && !alreadyPinned && !editingBuiltinPin}
      onClose={closeDiagram}
    />
  ) : (editingAgentId || isFactoryMode) && factoryFlow ? (
    <FactoryCanvas
      flow={factoryFlow}
      onMakeAgent={handleMakeAgent}
      onPinAgent={handlePinAgent}
      canMakeAgent={isFactoryMode && !selectedPinnedAgentId && !!chatFlow && !editingBuiltinPin}
      canPinAgent={isFactoryMode && !selectedPinnedAgentId && !!chatFlow && !alreadyPinned && !editingBuiltinPin}
      canEditAgent={isFactoryMode && !selectedPinnedAgentId && !!editingBuiltinPin}
      builtinPinId={viewedBuiltinId}
      builtinPinned={viewedBuiltinId ? pinnedBuiltins.has(viewedBuiltinId) : false}
      onToggleBuiltinPin={toggleBuiltinPin}
      isModifiedFromImplementation={isDiagramModified}
      onEditAgent={handleEditAgent}
      runEnabled={runMetadataAgent?.enabled}
      runSchedule={runMetadataAgent?.schedule}
      runLastRunAt={runMetadataAgent?.last_run_at ?? null}
      runLastRunStatus={runMetadataAgent?.last_run_status ?? null}
      runLastRunMessage={runMetadataAgent?.last_run_message ?? null}
      onToggleRunning={runMetadataAgent ? handleToggleRunning : undefined}
      tickHistory={runMetadataAgent?.tickHistory}
      tickIntervalSeconds={runMetadataAgent?.tickIntervalSeconds}
      tickMaxRuntimeSeconds={runMetadataAgent?.tickMaxRuntimeSeconds}
      onClose={closeDiagram}
    />
  ) : null;

  // Read-only diagram for an AUTO chat that emitted a process map / flow inline.
  // No Make/Pin/Edit affordances — selecting the Agent Factory agent is how a
  // user turns one of these into an editable, pinnable agent. Desktop-only (the
  // mobile shell renders only the chat panel, like the factory canvas).
  const autoDiagramCanvas = showAutoDiagram ? (
    autoProcessMap ? (
      <ProcessMapCanvas
        doc={autoProcessMap}
        canMakeAgent={false}
        canPinAgent={false}
        onClose={() => setClosedAutoDiagramRunId(chat.activeRunId)}
      />
    ) : autoFlow ? (
      <FactoryCanvas
        flow={autoFlow}
        onClose={() => setClosedAutoDiagramRunId(chat.activeRunId)}
      />
    ) : null
  ) : null;

  // The Idea Agent's idea list, shown when the user toggles it on while an Idea
  // Agent is active. Desktop fills the right pane (in place of the iframe);
  // mobile renders it as a full-screen overlay (like the diagram). Shared here.
  const ideaListEl = ideasView && isIdeaAgentActive ? (
    <IdeaList ideas={ideas} onDelete={handleDeleteIdea} onClose={() => setIdeasView(false)} />
  ) : null;

  // Small transient confirmation shown after a capture. Rendered fixed so it
  // floats over whatever is on screen (chat / iframe / ideas list).
  const ideaToastEl = ideaToast ? (
    <div className={`alty-idea-toast ${ideaToast.tone}`} role="status" aria-live="polite">
      <i className="fas fa-lightbulb" aria-hidden="true" />
      <span>{ideaToast.text}</span>
    </div>
  ) : null;

  // The apartment finder's saved cards, shown when the user toggles them on
  // while the apartment agent is active. Same right-pane (desktop) / overlay
  // (mobile) slot as the idea list — only one of the two can be active at once
  // since they're keyed off the single selected agent.
  const apartmentListEl = apartmentsView && isApartmentAgentActive ? (
    <ApartmentList apartments={apartments} onDelete={handleDeleteApartment} onClose={() => setApartmentsView(false)} />
  ) : null;
  const apartmentToastEl = apartmentToast ? (
    <div className={`alty-idea-toast ${apartmentToast.tone}`} role="status" aria-live="polite">
      <i className="fas fa-building" aria-hidden="true" />
      <span>{apartmentToast.text}</span>
    </div>
  ) : null;

  // The Customized BMad Agent Flow's workflow tabs — same right-pane (desktop) /
  // overlay (mobile) slot as the idea/apartment lists. Only one of these views
  // is ever active since they're keyed off the single selected agent.
  const bmadWorkflowEl = bmadView && isBmadAgentActive && bmadWorkflow ? (
    <BmadWorkflowView
      workflow={bmadWorkflow}
      buildState={bmadBuildState}
      onBuild={handleBuildBmadWorkflow}
      onOpenChat={(runId) => void chat.attachChat(runId)}
      onClose={() => setBmadView(false)}
      busy={chat.busy || bmadBuildBusy}
    />
  ) : null;

  // The Goal Discovery brief, shown when the user has the discovery view open
  // while the discovery agent is active and a brief exists. Desktop fills the
  // right pane (in place of the iframe); mobile renders it as a full-screen
  // overlay (like the diagram / lists). Shared here so both branches use it.
  const discoveryCanvasEl = discoveryView && isDiscoveryAgentActive && discoveryDoc ? (
    <DiscoveryCanvas doc={discoveryDoc} onClose={() => setDiscoveryView(false)} />
  ) : null;
  // Only offer the header toggle once there's a brief to view.
  const canShowDiscovery = isDiscoveryAgentActive && !!discoveryDoc;

  // Workspace canvas — the AI's display surface when no env tab is selected.
  // Renders the latest emitted `alty_canvas` payload, or an empty-state hint
  // until the AI puts something there. Sits in the default left-pane slot
  // (after all the agent-specific surfaces), so other views still take
  // priority if they're active. Close handler (`reopenFirstEnvTab`) lives
  // above the auth-gate returns so the hook order is stable.
  const workspaceCanvasEl = canvasMode ? (
    <WorkspaceCanvas
      content={canvasContent}
      badge={canvasBadge}
      multiEnv={config.tabs.length > 1}
      onClose={reopenFirstEnvTab}
    />
  ) : null;

  // On a phone the chat panel is the default full-bleed view, but a header
  // toggle now flips to an "app view" that shows the same environment iframes
  // (and the Online Dev MVP dev iframe) the desktop shell renders. The iframe
  // area is mounted once the user first opens it and kept alive behind a thin
  // switch bar; the `app-view` class on the shell hides the chat panel host and
  // offsets the fixed iframes below the bar (see styles.css).
  if (isMobile) {
    const showApp = mobileView === 'app';
    const showMobileDevPanel = showOnlineDevPanel && !!chat.devEnv;
    return (
      <main className={`alty-shell alty-shell-mobile${showApp ? ' app-view' : ''}`}>
        {mobileAppMounted ? (
          <div className="alty-mobile-app" hidden={!showApp} aria-hidden={!showApp}>
            <div className="alty-mobile-app-bar">
              <div className="alty-mobile-env-tabs">
                {config.tabs.map((tab) => {
                  const active = tab.id === activeTabId;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      className={active ? 'alty-mobile-env-tab active' : 'alty-mobile-env-tab'}
                      onClick={() => selectTab(tab)}
                      title={tab.label}
                      aria-label={tab.label}
                      aria-pressed={active}
                    >
                      {tab.icon === 'claude'
                        ? <ClaudeIcon />
                        : <i className={`fas ${tab.icon || 'fa-circle'}`} aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
              {/* Back to the chat view. Sits on the right, just left of the cog
                  — the mirror of the chat header's "Online" toggle. Icon-only on
                  mobile (no text label). */}
              <button
                type="button"
                className="alty-mobile-app-back"
                onClick={() => setMobileView('chat')}
                title="Back to chat"
                aria-label="Back to chat"
              >
                <i className="fas fa-comments" aria-hidden="true" />
              </button>
              {/* Settings reachable from the app view too: pop back to the chat
                  and open its cog popover (which lives in the chat panel). */}
              <button
                type="button"
                className="alty-mobile-app-cog"
                onClick={() => { setMobileView('chat'); setMobileSettingsOpen(true); }}
                title="Settings"
                aria-label="Settings"
              >
                <i className="fas fa-cog" aria-hidden="true" />
              </button>
            </div>
            <div className="alty-mobile-app-body">
              {showMobileDevPanel && chat.devEnv ? (
                <OnlineDevPanel
                  devEnv={chat.devEnv}
                  runId={chat.activeRunId}
                  onClose={() => setClosedDevEnvRunId(chat.activeRunId)}
                  onShip={(prompt: string) => chat.sendMessage(prompt)}
                  busy={chat.busy}
                  chatTitle={chat.chats.find((c) => c.runId === chat.activeRunId)?.title || undefined}
                />
              ) : workspaceCanvasEl ? (
                workspaceCanvasEl
              ) : (
                config.tabs.map((tab) =>
                  loadedTabIds.has(tab.id) ? (
                    <AppFrame
                      key={tab.id}
                      ref={setFrameRef(tab.id)}
                      src={tabSrcById[tab.id] || tab.url}
                      title={`${tab.label} application`}
                      hidden={tab.id !== activeTabId}
                      isPrimary={tab.id === activeTabId}
                      onLoad={() => handleFrameLoad(tab.id)}
                    />
                  ) : null,
                )
              )}
            </div>
          </div>
        ) : null}
        <AltYPanelHost
          isOpen
          mobile
          chat={chatWithAutoRouting}
          appFrame={activeFrame}
          tabs={config.tabs}
          activeTabId={activeTabId}
          onSelectTab={selectTab}
          onShowApp={() => {
            setMobileAppMounted(true);
            setMobileView('app');
            // In an online-dev chat, jump to THIS chat's dev environment rather
            // than the standard server iframes — reverse a prior dismissal so
            // the dev panel re-appears instead of falling back to AI/Testing/etc.
            if (isOnlineDevMvpMode) setClosedDevEnvRunId(null);
          }}
          canReopenDiagram={canReopenFactoryDiagram}
          onReopenDiagram={reopenDiagram}
          canShowIdeas={isIdeaAgentActive}
          ideasOpen={ideasView}
          onToggleIdeas={toggleIdeasView}
          canShowApartments={isApartmentAgentActive}
          apartmentsOpen={apartmentsView}
          onToggleApartments={toggleApartmentsView}
          canShowBmadWorkflow={isBmadAgentActive}
          bmadWorkflowOpen={bmadView}
          onToggleBmadWorkflow={toggleBmadView}
          canShowDiscovery={canShowDiscovery}
          discoveryOpen={discoveryView}
          onToggleDiscovery={toggleDiscoveryView}
          settingsOpen={mobileSettingsOpen}
          onSettingsOpenChange={setMobileSettingsOpen}
          canvasOnly={canvasOnly}
          onCanvasOnlyChange={setCanvasOnly}
          canvasContent={canvasContent}
          canvasBadge={canvasBadge}
          pinnedAgents={allPinnedAgents}
          selectedAgentId={selectedAgentId}
          selectedPinnedAgentId={visiblePinnedAgentId}
          onSelectPinnedAgent={handleSelectPinnedAgent}
          onEditPinnedAgent={handleEditPinnedAgent}
          onDeletePinnedAgent={handleDeletePinnedAgent}
          onStartNewChat={handleStartNewChat}
          onStartSubchat={handleStartSubchat}
          onBrokerRestartReady={refreshUserAgents}
          improveAgentLabel={improveAgentTarget?.label ?? null}
          onImproveAgent={improveAgentTarget ? handleImproveAgent : undefined}
          selectedUserIds={selectedUserIds}
          onSelectUserIds={handleSelectUsers}
        />
        {/* Tapping a pin's edit pen surfaces its diagram as a full-screen
            overlay above the chat panel + drawer (desktop renders it behind the
            panel; on a phone there's no room beside it). Its own close button
            (onClose) clears the edit gesture and drops back to the chat. */}
        {diagramCanvas ? (
          <div className="alty-mobile-diagram">{diagramCanvas}</div>
        ) : null}
        {/* Idea Agent list — same full-screen overlay slot as the diagram (so it
            clears the panel's stacking context); the header's ideas toggle opens
            it and its own close button drops back to the conversation. */}
        {ideaListEl ? (
          <div className="alty-mobile-diagram">{ideaListEl}</div>
        ) : null}
        {/* Apartment finder cards — same full-screen overlay slot. */}
        {apartmentListEl ? (
          <div className="alty-mobile-diagram">{apartmentListEl}</div>
        ) : null}
        {/* BMad workflow tabs — same full-screen overlay slot. */}
        {bmadWorkflowEl ? (
          <div className="alty-mobile-diagram">{bmadWorkflowEl}</div>
        ) : null}
        {/* Goal Discovery brief — same full-screen overlay slot. The header's
            bullseye toggle opens it; its own close button drops back to chat. */}
        {discoveryCanvasEl ? (
          <div className="alty-mobile-diagram">{discoveryCanvasEl}</div>
        ) : null}
        {ideaToastEl}
        {apartmentToastEl}
      </main>
    );
  }

  return (
    <main className="alty-shell">
      {showOnlineDevPanel && chat.devEnv ? (
        <OnlineDevPanel
          devEnv={chat.devEnv}
          runId={chat.activeRunId}
          onClose={() => setClosedDevEnvRunId(chat.activeRunId)}
          onShip={(prompt: string) => chat.sendMessage(prompt)}
          busy={chat.busy}
          chatTitle={chat.chats.find((c) => c.runId === chat.activeRunId)?.title || undefined}
        />
      ) : diagramCanvas ? (
        diagramCanvas
      ) : autoDiagramCanvas ? (
        autoDiagramCanvas
      ) : ideaListEl ? (
        ideaListEl
      ) : apartmentListEl ? (
        apartmentListEl
      ) : bmadWorkflowEl ? (
        bmadWorkflowEl
      ) : discoveryCanvasEl ? (
        discoveryCanvasEl
      ) : workspaceCanvasEl ? (
        workspaceCanvasEl
      ) : (
        config.tabs.map((tab) =>
          loadedTabIds.has(tab.id) ? (
            <AppFrame
              key={tab.id}
              ref={setFrameRef(tab.id)}
              src={tabSrcById[tab.id] || tab.url}
              title={`${tab.label} application`}
              hidden={tab.id !== activeTabId}
              isPrimary={tab.id === activeTabId}
              onLoad={() => handleFrameLoad(tab.id)}
            />
          ) : null,
        )
      )}
      <AltYPanelHost
        isOpen={altYState.isOpen}
        chat={chatWithAutoRouting}
        appFrame={activeFrame}
        tabs={config.tabs}
        activeTabId={activeTabId}
        onSelectTab={selectTab}
        canShowIdeas={isIdeaAgentActive}
        ideasOpen={ideasView}
        onToggleIdeas={toggleIdeasView}
        canShowApartments={isApartmentAgentActive}
        apartmentsOpen={apartmentsView}
        onToggleApartments={toggleApartmentsView}
        canShowBmadWorkflow={isBmadAgentActive}
        bmadWorkflowOpen={bmadView}
        onToggleBmadWorkflow={toggleBmadView}
        canShowDiscovery={canShowDiscovery}
        discoveryOpen={discoveryView}
        onToggleDiscovery={toggleDiscoveryView}
        canvasOnly={canvasOnly}
        onCanvasOnlyChange={setCanvasOnly}
        canvasContent={canvasContent}
        canvasBadge={canvasBadge}
        pinnedAgents={allPinnedAgents}
        selectedAgentId={selectedAgentId}
        selectedPinnedAgentId={visiblePinnedAgentId}
        onSelectPinnedAgent={handleSelectPinnedAgent}
        onEditPinnedAgent={handleEditPinnedAgent}
        onDeletePinnedAgent={handleDeletePinnedAgent}
        onStartNewChat={handleStartNewChat}
        onStartSubchat={handleStartSubchat}
        onBrokerRestartReady={refreshUserAgents}
        improveAgentLabel={improveAgentTarget?.label ?? null}
        onImproveAgent={improveAgentTarget ? handleImproveAgent : undefined}
        selectedUserIds={selectedUserIds}
        onSelectUserIds={handleSelectUsers}
      />
      {ideaToastEl}
      {apartmentToastEl}
    </main>
  );
}

function BootstrapLoading({ message = 'Loading…' }: { message?: string }) {
  return (
    <div className="alty-login">
      <div className="alty-login-card">
        <div className="alty-login-brand">Alt-Y</div>
        <p>{message}</p>
      </div>
    </div>
  );
}

export function App() {
  const [bootstrap, setBootstrap] = useState<AltYBootstrap | null>(() => readSynchronousAltYBootstrap());
  const [loadError, setLoadError] = useState('');

  const reloadBootstrap = useCallback(async () => {
    const nextBootstrap = await loadAltYBootstrap();
    setBootstrap(nextBootstrap);
    setLoadError('');
    return nextBootstrap;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void loadAltYBootstrap().then((nextBootstrap) => {
      if (cancelled) return;
      setBootstrap(nextBootstrap);
      setLoadError('');
    }, (error) => {
      if (cancelled) return;
      setLoadError((error as Error).message || 'Could not load Alt-Y bootstrap.');
      setBootstrap(readSynchronousAltYBootstrap());
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!bootstrap) {
    return <BootstrapLoading message={loadError || 'Loading…'} />;
  }

  return <AltYShellApp bootstrap={bootstrap} reloadBootstrap={reloadBootstrap} />;
}
